import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, 
  Clock,
  Clock3,
  LogOut, 
  TrendingUp,
  Calendar,
  User,
  Home,
  Shield,
  UserPlus,
  UserMinus,
  UserCog,
  X,
  Trash2,
  Edit2,
  FileText,
  Download,
  Bell,
  BellOff,
  CheckCheck,
  LogIn,
  Edit3,
  DollarSign,
  Settings,
  CalendarDays,
  Eye,
  Briefcase,
  Package,
  FileSignature,
  ChevronDown,
  ChevronUp,
  Phone,
  MapPin,
  CheckCircle,
  AlertCircle,
  Archive,
  ArrowUpDown,
  ArrowUp,
  Upload,
  Camera,
  Image as ImageIcon,
  Pencil,
  Search,
  Filter,
  Monitor,
  Save,
  RefreshCw,
  MessageSquare,
  Mail,
  CreditCard,
  Key,
  Navigation,
  Play,
  Pause,
  Square,
  ClipboardCheck,
  Inbox,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  Video,
  MoreVertical,
  Grid3x3,
  FolderOpen,
  Car,
  BarChart3,
  Receipt,
  Tv,
  LayoutGrid
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { DayPicker } from "react-day-picker";
import { toast } from "sonner";
import axios from "axios";
import { useHaptics } from "@/hooks/useHaptics";
import LiveActivityService from "@/services/LiveActivityService";
import GPSMileageTracker from "@/components/admin/sections/GPSMileageTracker";
import { useDashboardTheme } from "@/hooks/useDashboardTheme";
import PaymentRecordsSection from "@/components/admin/sections/PaymentRecordsSection";
import PayrollHistorySection from "@/components/admin/sections/PayrollHistorySection";
import FormSubmissionsSection from "@/components/admin/sections/FormSubmissionsSection";
import MessagesSection from "@/components/admin/sections/MessagesSection";

import AllEmployeesSection from "@/components/admin/sections/AllEmployeesSection";
import HoursByEmployeeSection from "@/components/admin/sections/HoursByEmployeeSection";
import PasswordManagementSection from "@/components/admin/sections/PasswordManagementSection";
import FinancialsSection from "@/components/admin/sections/FinancialsSection";
import TaxReturnsArchiveSection from "@/components/admin/sections/TaxReturnsArchiveSection";
import SalesDataSection from "@/components/admin/sections/SalesDataSection";
import CSVUpdateButton from "@/components/admin/sections/CSVUpdateButton";
import PullListSection from "@/components/admin/sections/PullListSection";
import TaxesSection from "@/components/admin/sections/TaxesSection";
import InterviewSchedulerSection from "@/components/admin/sections/InterviewSchedulerSection";
import ApplicantTestsSection from "@/components/admin/sections/ApplicantTestsSection";
import EmployeeTerminationsSection from "@/components/admin/sections/EmployeeTerminationsSection";
import PendingDocumentsSection from "@/components/admin/sections/PendingDocumentsSection";
import SendApplicationLinkSection from "@/components/admin/sections/SendApplicationLinkSection";
import AllApplicationsSection from "@/components/admin/sections/AllApplicationsSection";
import ShippingLabelsSection from "@/components/admin/sections/ShippingLabelsSection";
import OrderAssignmentSection from "@/components/admin/sections/OrderAssignmentSection";
import { setAppBadge } from "@/utils/appBadge";
import TrainingSection from "@/components/admin/sections/TrainingSection";
import BusinessFilesPage from "@/components/admin/sections/BusinessFilesPage";
import WhatnotShowsSection from "@/components/admin/sections/WhatnotShowsSection";
import AIAssistant from "@/components/ai/AIAssistant";
import DashboardGroup from "@/components/admin/DashboardGroup";
import CompactEmployeeTracker from "@/components/admin/CompactEmployeeTracker";
import WebPushSettings from "@/components/WebPushSettings";
import { WidgetScriptsSection } from "@/components/admin/sections/WidgetScriptsSection";
import AdminFullScreenMessaging from "@/components/AdminFullScreenMessaging";
import ShiftReportModal from "@/components/admin/modals/ShiftReportModal";
import PayrollModal from "@/components/admin/modals/PayrollModal";
import TimeEntryModal from "@/components/admin/modals/TimeEntryModal";
import FormSubmissionModal from "@/components/admin/modals/FormSubmissionModal";
import { AddEmployeeModal, RemoveEmployeeModal } from "@/components/admin/modals/EmployeeModals";
import EmployeePortalViewModal from "@/components/admin/modals/EmployeePortalViewModal";
import ConsignorPortalViewModal from "@/components/admin/modals/ConsignorPortalViewModal";
import W9ViewerModal from "@/components/admin/modals/W9ViewerModal";
import PortalW9Modal from "@/components/admin/modals/PortalW9Modal";
import EmployeeShiftsModal from "@/components/admin/modals/EmployeeShiftsModal";
import EditEmployeeModal from "@/components/admin/modals/EditEmployeeModal";
import { formatHoursToHMS, roundHoursToMinute } from "@/lib/utils";
import { formatPeriodDay } from "@/utils/periodDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Haptic feedback
  const { buttonPress, heavyPress, lightTap, successFeedback, errorFeedback, warningFeedback } = useHaptics();
  
  const [user, setUser] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeClockStatuses, setEmployeeClockStatuses] = useState({}); // Track which employees are clocked in
  const [timeEntries, setTimeEntries] = useState([]);
  const [summary, setSummary] = useState({ 
    total_employees: 0, 
    total_hours: 0, 
    total_shifts: 0,
    by_employee: []
  });
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showRemoveEmployee, setShowRemoveEmployee] = useState(false);
  const [selectedEmployeeToRemove, setSelectedEmployeeToRemove] = useState("");
  const [newEmployee, setNewEmployee] = useState({ name: "", email: "", phone: "", role: "employee", admin_code: "" });
  const [addingEmployee, setAddingEmployee] = useState(false);
  const [removingEmployee, setRemovingEmployee] = useState(false);
  const [selectedJobApp, setSelectedJobApp] = useState("");
  const [newEmployeeW9File, setNewEmployeeW9File] = useState(null);
  const [uploadingNewEmployeeW9, setUploadingNewEmployeeW9] = useState(false);
  
  // Employee details modal state
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeShifts, setEmployeeShifts] = useState([]);
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
  const [editEmployeeW9File, setEditEmployeeW9File] = useState(null);
  const [uploadingEditW9, setUploadingEditW9] = useState(false);
  
  // Report state
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    period_type: "custom",
    period_index: 0,
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
    start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    employee_id: ""
  });
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Remote session alert badge
  const [remoteAlertCount, setRemoteAlertCount] = useState(0);
  const [remoteActiveCount, setRemoteActiveCount] = useState(0);
  const [pendingVideoCallCount, setPendingVideoCallCount] = useState(0);
  
  // Payroll quick view state
  const [showPayrollQuickView, setShowPayrollQuickView] = useState(false);
  const [payrollPeriodIndex, setPayrollPeriodIndex] = useState(0); // 0 = current, -1 = previous
  const payrollQuickViewRef = useRef(null);
  const [forceOpenPayrollGroup, setForceOpenPayrollGroup] = useState(false);
  const [forceOpenClockedInTracker, setForceOpenClockedInTracker] = useState(false);
  
  // Touch/scroll swipe tracking for payroll quick view
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const lastWheelTime = useRef(0); // Debounce trackpad scrolls
  
  // Track data updates for real-time sync
  const [lastDataUpdate, setLastDataUpdate] = useState(Date.now());
  
  // Current admin info
  const [currentAdminName, setCurrentAdminName] = useState("Administrator");
  
  // Edit time entry state
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editFormData, setEditFormData] = useState({
    clock_in: "",
    clock_out: "",
    total_hours: ""
  });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Add time entry state
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [newEntryData, setNewEntryData] = useState({
    employee_id: "",
    clock_in: "",
    clock_out: ""
  });
  const [addingEntry, setAddingEntry] = useState(false);
  
  // Welcome email preview state
  const [showWelcomeEmailPreview, setShowWelcomeEmailPreview] = useState(false);
  const [welcomeEmailData, setWelcomeEmailData] = useState(null);
  
  // Payroll state
  const [showPayroll, setShowPayroll] = useState(false);
  const [payrollSettings, setPayrollSettings] = useState({
    pay_period_start_date: "",
    default_hourly_rate: 15.00
  });
  const [payrollFilters, setPayrollFilters] = useState({
    period_type: "biweekly",
    period_index: 0,
    hourly_rate: "",
    employee_id: "",
    custom_start: "",
    custom_end: ""
  });
  const [payrollReport, setPayrollReport] = useState(null);
  
  // Employee rate editing state
  const [editingRateId, setEditingRateId] = useState(null);
  const [editingRateValue, setEditingRateValue] = useState("");
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [showPayrollSettings, setShowPayrollSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Edit employee modal state
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editEmployeeData, setEditEmployeeData] = useState({
    name: "",
    email: "",
    role: "employee",
    hourly_rate: "",
    phone: "",
    start_date: "",
    admin_code: ""
  });

  // Form submissions state
  const [formSubmissions, setFormSubmissions] = useState({
    jobApplications: [],
    consignmentAgreements: []
  });
  const [formsSummary, setFormsSummary] = useState({
    job_applications: { total: 0, new: 0 },
    consignment_agreements: { total: 0, new: 0 }
  });
  const [paymentMethodChanges, setPaymentMethodChanges] = useState([]);
  const [itemAdditions, setItemAdditions] = useState([]);
  const [activeFormTab, setActiveFormTab] = useState("job_applications");
  const [loadingForms, setLoadingForms] = useState(false);
  
  // Email settings state
  const [emailStatus, setEmailStatus] = useState({ enabled: false, mode: 'mocked', message: '' });
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Full-screen messaging state
  const [showFullScreenMessages, setShowFullScreenMessages] = useState(false);
  const [adminUnreadMessageCount, setAdminUnreadMessageCount] = useState(0);
  const [messagesMuted, setMessagesMuted] = useState(() => {
    return localStorage.getItem('thrifty_curator_admin_messages_muted') === 'true';
  });
  // Message theme preference (light or dark) - shared with employees
  const [messagesTheme, setMessagesTheme] = useState(() => {
    return localStorage.getItem('thrifty_curator_messages_theme') || 'light';
  });
  const { isDark, toggleTheme } = useDashboardTheme(); // kept for messages only
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showFormsSection, setShowFormsSection] = useState(false);
  const [formSearchQuery, setFormSearchQuery] = useState(""); // Search for form submissions
  const [formStatusFilter, setFormStatusFilter] = useState("all"); // Filter by status

  // W-9 Review state
  const [pendingW9s, setPendingW9s] = useState([]);
  const [loadingPendingW9s, setLoadingPendingW9s] = useState(false);
  const [reviewingW9, setReviewingW9] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showW9ReviewSection, setShowW9ReviewSection] = useState(false);

  // Contractor Agreement Review state
  const [pendingAgreements, setPendingAgreements] = useState([]);
  const [loadingPendingAgreements, setLoadingPendingAgreements] = useState(false);
  const [reviewingAgreement, setReviewingAgreement] = useState(null);
  const [agreementRejectReason, setAgreementRejectReason] = useState("");

  // W-8BEN Review state  
  const [pendingW8bens, setPendingW8bens] = useState([]);
  const [loadingPendingW8bens, setLoadingPendingW8bens] = useState(false);
  const [reviewingW8ben, setReviewingW8ben] = useState(null);
  const [w8benRejectReason, setW8benRejectReason] = useState("");

  // Collapsible sections state - all collapsed by default
  const [showStatsSection, setShowStatsSection] = useState(false);
  const [showHoursByEmployee, setShowHoursByEmployee] = useState(false);
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const [showTimeEntries, setShowTimeEntries] = useState(false);
  
  // Master refresh state
  const [masterRefreshing, setMasterRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(Date.now()); // Key to trigger child refreshes

  // Payroll summary state for overview
  const [payrollSummary, setPayrollSummary] = useState({
    current_period: { amount: 0, hours: 0, start: '', end: '' },
    month_total: 0,
    year_total: 0
  });

  // Sorting state for all tables
  const [sortConfig, setSortConfig] = useState({
    hoursByEmployee: { key: 'hours', direction: 'desc' },
    allEmployees: { key: 'created_at', direction: 'desc' },
    timeEntries: { key: 'clock_in', direction: 'desc' },
    jobApplications: { key: 'submitted_at', direction: 'desc' },
    consignmentAgreements: { key: 'submitted_at', direction: 'desc' }
  });

  // Employee portal view state
  const [showEmployeePortal, setShowEmployeePortal] = useState(false);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [employeePortalData, setEmployeePortalData] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [employeeClockStatus, setEmployeeClockStatus] = useState(null);
  const [showClockConfirm, setShowClockConfirm] = useState(null); // 'in' or 'out'
  const [clockingEmployee, setClockingEmployee] = useState(false);
  const [portalElapsedTime, setPortalElapsedTime] = useState(0); // Timer for admin portal view

  // Consignor portal view state
  const [showConsignorPortal, setShowConsignorPortal] = useState(false);
  const [viewingConsignor, setViewingConsignor] = useState(null);
  const [consignorPortalData, setConsignorPortalData] = useState(null);
  const [loadingConsignorPortal, setLoadingConsignorPortal] = useState(false);

  // W-9 Viewer Modal state
  const [showW9ViewerModal, setShowW9ViewerModal] = useState(false);
  const [viewingW9, setViewingW9] = useState(null);
  const [loadingW9Viewer, setLoadingW9Viewer] = useState(false);
  const [employeeW9List, setEmployeeW9List] = useState([]);
  const [selectedW9Index, setSelectedW9Index] = useState(0);
  const [w9ViewerFromPortal, setW9ViewerFromPortal] = useState(false);

  // Portal W-9 Modal state (dark theme)
  const [showPortalW9Modal, setShowPortalW9Modal] = useState(false);
  const [portalW9Docs, setPortalW9Docs] = useState([]);
  const [loadingPortalW9, setLoadingPortalW9] = useState(false);
  const [previewingPortalW9, setPreviewingPortalW9] = useState(null);

  // Employee shifts management state (for Hours by Employee section)
  const [showEmployeeShiftsModal, setShowEmployeeShiftsModal] = useState(null);
  const [loadingShifts, setLoadingShifts] = useState(false);

  // W-9 upload state
  const [uploadingW9, setUploadingW9] = useState(null);
  const w9InputRef = useRef(null);

  // Back to top button state
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  
  // GPS Trip tracking
  const [forceOpenOperations, setForceOpenOperations] = useState(false); // Navigate to operations page
  const gpsTrackerRef = useRef(null); // Reference to scroll to GPS section
  const [tripState, setTripState] = useState({ active: false, paused: false }); // Synced from GPSMileageTracker
  const [tripStarting, setTripStarting] = useState(false); // Loading state for header button
  
  // Hidden email settings trigger - triple click on title
  const titleClickCount = useRef(0);
  const titleClickTimer = useRef(null);

  // Page navigation state for multi-page dashboard
  const [activePage, setActivePageRaw] = useState("home");
  const [operationsSubPage, setOperationsSubPage] = useState(null); // null = tiles, "orders", "mileage", "sales", "taxes"
  
  // Wrap setActivePage to reset operations sub-page when navigating
  const setActivePage = useCallback((page) => {
    setActivePageRaw(page);
    if (page !== "operations") setOperationsSubPage(null);
  }, []);

  // Business owner emails - only these users can assign admin roles
  const OWNER_EMAILS = ["matthewjesusguzman1@gmail.com", "euniceguzman@thriftycurator.com"];

  // Load admin name from localStorage on mount
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.name) {
          setCurrentAdminName(user.name);
        }
        // Check if current user is a business owner
        if (user.email && OWNER_EMAILS.includes(user.email.toLowerCase())) {
          setIsOwner(true);
        }
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
  }, []);

  // Handle scroll for back to top button with hysteresis to prevent flickering
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          setShowBackToTop(prev => {
            // Show at 400px, hide at 300px (hysteresis buffer)
            if (!prev && scrollY > 400) return true;
            if (prev && scrollY < 300) return false;
            return prev;
          });
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper function to calculate biweekly period from a start date
  // Helper to get the first Monday of a given year
  const getFirstMondayOfYear = (year) => {
    const jan1 = new Date(year, 0, 1);
    const dayOfWeek = jan1.getDay(); // 0 = Sunday, 1 = Monday, etc.
    // Calculate days to add to get to first Monday
    const daysToAdd = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
    return new Date(year, 0, 1 + daysToAdd);
  };

  // Calculate bi-weekly pay period based on first Monday of the year
  // The pay period is ALWAYS anchored to the first Monday of the current year
  const calculateBiweeklyPeriod = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Get the first Monday of the current year as the anchor
    const firstMonday = getFirstMondayOfYear(today.getFullYear());
    
    // Calculate how many complete 14-day periods have passed since first Monday
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysDiff = Math.floor((today - firstMonday) / msPerDay);
    const periodNumber = Math.floor(daysDiff / 14);
    
    // Calculate the current period's start and end
    const periodStart = new Date(firstMonday.getTime() + (periodNumber * 14 * msPerDay));
    const periodEnd = new Date(periodStart.getTime() + (13 * msPerDay));
    
    return {
      start: periodStart,
      end: periodEnd,
      periodNumber: periodNumber + 1,
      anchorDate: firstMonday
    };
  };

  // Helper to get report date range based on period type
  const getReportDateRange = () => {
    const { period_type, period_index, month, year, start_date, end_date } = reportFilters;
    
    if (period_type === "custom") {
      return { start: start_date, end: end_date };
    }
    
    if (period_type === "pay_period") {
      // Use payroll settings to calculate pay period
      if (!payrollSettings.pay_period_start_date) {
        return { start: start_date, end: end_date };
      }
      
      const startDateStr = payrollSettings.pay_period_start_date;
      const baseStart = new Date(startDateStr + 'T00:00:00');
      const today = new Date();
      const msPerDay = 24 * 60 * 60 * 1000;
      const daysDiff = Math.floor((today - baseStart) / msPerDay);
      const currentPeriodNum = Math.floor(daysDiff / 14);
      const targetPeriodNum = currentPeriodNum - period_index;
      
      const periodStart = new Date(baseStart.getTime() + (targetPeriodNum * 14 * msPerDay));
      const periodEnd = new Date(periodStart.getTime() + (13 * msPerDay));
      
      return {
        start: periodStart.toISOString().split('T')[0],
        end: periodEnd.toISOString().split('T')[0]
      };
    }
    
    if (period_type === "month") {
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);
      return {
        start: monthStart.toISOString().split('T')[0],
        end: monthEnd.toISOString().split('T')[0]
      };
    }
    
    if (period_type === "year") {
      return {
        start: `${year}-01-01`,
        end: `${year}-12-31`
      };
    }
    
    return { start: start_date, end: end_date };
  };

  const getAuthHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  }), []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications`, getAuthHeader());
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, [getAuthHeader]);

  // Fetch unread message count for the Messages icon badge
  const fetchMessageUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations/admin/unread-count`, getAuthHeader());
      setAdminUnreadMessageCount(response.data.unread_count || 0);
      setAppBadge(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch message unread count:", error);
    }
  }, [getAuthHeader]);

  const fetchPayrollSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/payroll/settings`, getAuthHeader());
      setPayrollSettings(response.data);
      setPayrollFilters(prev => ({
        ...prev,
        hourly_rate: response.data.default_hourly_rate?.toString() || "15.00"
      }));
    } catch (error) {
      console.error("Failed to fetch payroll settings:", error);
    }
  }, [getAuthHeader]);

  const fetchPayrollSummary = useCallback(async (periodIndex = 0) => {
    try {
      const response = await axios.get(`${API}/admin/payroll/summary?period_index=${periodIndex}`, getAuthHeader());
      setPayrollSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch payroll summary:", error);
    }
  }, [getAuthHeader]);

  // Combined refresh function for data that affects payroll
  const refreshAllData = useCallback(async () => {
    await Promise.all([
      fetchData(),
      fetchPayrollSummary()
    ]);
  }, [fetchPayrollSummary]);

  const fetchFormSubmissions = useCallback(async () => {
    setLoadingForms(true);
    try {
      const [jobAppsRes, agreementsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/forms/job-applications`, getAuthHeader()),
        axios.get(`${API}/admin/forms/consignment-agreements`, getAuthHeader()),
        axios.get(`${API}/admin/forms/summary`, getAuthHeader())
      ]);

      setFormSubmissions({
        jobApplications: jobAppsRes.data,
        consignmentAgreements: agreementsRes.data
      });
      setFormsSummary(summaryRes.data);
    } catch (error) {
      console.error("Failed to fetch form submissions:", error);
    } finally {
      setLoadingForms(false);
    }
  }, [getAuthHeader]);

  const fetchPaymentMethodChanges = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/forms/payment-method-changes`, getAuthHeader());
      setPaymentMethodChanges(response.data);
    } catch (error) {
      console.error("Failed to fetch payment method changes:", error);
    }
  }, [getAuthHeader]);

  const fetchItemAdditions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/forms/consignment-item-additions`, getAuthHeader());
      setItemAdditions(response.data);
    } catch (error) {
      console.error("Failed to fetch item additions:", error);
    }
  }, [getAuthHeader]);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (payrollQuickViewRef.current && !payrollQuickViewRef.current.contains(event.target)) {
        setShowPayrollQuickView(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-mark notifications as read when panel is opened
  useEffect(() => {
    if (showNotifications && unreadCount > 0) {
      // Mark all as read after a brief delay so user sees them first
      const timer = setTimeout(async () => {
        try {
          await axios.post(`${API}/admin/notifications/mark-read`, {}, getAuthHeader());
          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
          setUnreadCount(0);
        } catch (error) {
          console.error("Failed to auto-mark notifications as read:", error);
        }
      }, 500); // 0.5 second delay
      
      return () => clearTimeout(timer);
    }
  }, [showNotifications, unreadCount, getAuthHeader]);

  // Handle notification click - scroll to section and open item
  const handleNotificationClick = async (notification) => {
    // Close the notification panel
    setShowNotifications(false);
    
    // Small delay to let panel close
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Helper function to expand a DashboardGroup if collapsed
    const expandGroupIfNeeded = async (groupTestId) => {
      const groupToggle = document.querySelector(`[data-testid="${groupTestId}-toggle"]`);
      if (groupToggle) {
        // Check if group is collapsed (chevron pointing down)
        const chevronDown = groupToggle.querySelector('svg.lucide-chevron-down');
        if (chevronDown) {
          groupToggle.click();
          await new Promise(resolve => setTimeout(resolve, 400));
        }
      }
    };
    
    // Helper function to expand section only if collapsed
    const expandSectionIfNeeded = async (sectionSelector, toggleSelector) => {
      const section = document.querySelector(sectionSelector);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        
        // Check if section content is visible (expanded)
        const toggle = document.querySelector(toggleSelector);
        if (toggle) {
          // Check for ChevronDown icon which indicates collapsed state
          const chevronDown = toggle.querySelector('svg.lucide-chevron-down');
          // If ChevronDown is present, section is collapsed - click to expand
          if (chevronDown) {
            await new Promise(resolve => setTimeout(resolve, 300));
            toggle.click();
          }
        }
      }
    };
    
    // Determine which section to scroll to and what to open
    switch (notification.type) {
      case 'clock_in':
        // Clock IN - expand the green Clocked In tracker (visible on home)
        setActivePage("home");
        setForceOpenClockedInTracker(true);
        break;
        
      case 'clock_out':
        // Clock OUT - show Hours section on home
        setActivePage("home");
        fetchPayrollSummary();
        await new Promise(resolve => setTimeout(resolve, 300));
        document.querySelector('[data-testid="hours-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
        
      case 'w9_submission':
      case 'w9_submitted':
        // Navigate to team page
        setActivePage("team");
        await new Promise(resolve => setTimeout(resolve, 300));
        document.querySelector('[data-testid="employees-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
        
      case 'new_message':
      case 'employee_message':
      case 'consignor_message':
        // Navigate to messages page
        setActivePage("messages");
        break;
        
      case 'job_application':
        // Navigate to hiring page
        setActivePage("hiring");
        break;

      case 'consignment_inquiry':
      case 'consignment_agreement':
        // Navigate to forms page
        setActivePage("forms");
        break;
      
      case 'payment_method_change':
      case 'consignment_items_added':
        // Navigate to forms page
        setActivePage("forms");
        break;
        
      default:
        // Default: scroll to top of dashboard
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    
    if (!token || !userData) {
      navigate("/login");
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }

    setUser(parsedUser);
    fetchData();
    fetchNotifications();
    fetchMessageUnreadCount(); // Fetch message unread count for header badge
    fetchPayrollSettings();
    fetchPayrollSummary();
    fetchFormSubmissions(); // Auto-fetch form submissions on page load
    
    // Check if we should open messages from URL param (e.g., from push notification)
    const openMessages = searchParams.get('openMessages') === 'true';
    const conversationIdFromUrl = searchParams.get('conversationId');
    
    if (openMessages) {
      setShowFullScreenMessages(true);
      // Store conversation ID to auto-select after messages load
      if (conversationIdFromUrl) {
        localStorage.setItem('pendingConversationId', conversationIdFromUrl);
      }
      // Clear the params from URL
      searchParams.delete('openMessages');
      searchParams.delete('conversationId');
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get('openNotifications') === 'true') {
      setShowNotifications(true);
      searchParams.delete('openNotifications');
      setSearchParams(searchParams, { replace: true });
    }
    
    // Register for push notifications (for clock-in/out alerts)
    const registerPush = async () => {
      try {
        await LiveActivityService.registerForPushNotifications(parsedUser.id, "admin");
        console.log('Admin registered for push notifications');
      } catch (e) {
        console.log('Push registration skipped:', e);
      }
      
      // Also register for Web Push (browser/PWA notifications)
      try {
        if ('Notification' in window && 'serviceWorker' in navigator && Notification.permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          let subscription = await registration.pushManager.getSubscription();
          if (!subscription) {
            // Get VAPID key and subscribe
            const vapidRes = await axios.get(`${API}/web-push/vapid-public-key`);
            const vapidKey = vapidRes.data.publicKey;
            const urlBase64ToUint8Array = (base64String) => {
              const padding = '='.repeat((4 - base64String.length % 4) % 4);
              const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
              const rawData = window.atob(base64);
              const outputArray = new Uint8Array(rawData.length);
              for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
              return outputArray;
            };
            subscription = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(vapidKey)
            });
          }
          // Register/update subscription with backend
          const subJSON = subscription.toJSON();
          await axios.post(`${API}/web-push/subscribe`, {
            endpoint: subJSON.endpoint,
            keys: subJSON.keys,
            expirationTime: subJSON.expirationTime || null
          }, getAuthHeader());
          console.log('Admin registered for web push notifications');
        }
      } catch (e) {
        console.log('Web push registration skipped:', e);
      }
    };
    registerPush();
    
    // Poll for new notifications and data every 15 seconds
    const pollInterval = setInterval(() => {
      fetchNotifications();
      fetchMessageUnreadCount(); // Also poll for new messages
      fetchData(); // Also refresh main data to keep all sections in sync
      // Fetch remote session badge count
      axios.get(`${API}/remote-sessions/unread-count`, getAuthHeader())
        .then(res => { setRemoteAlertCount(res.data.alert_count || 0); setRemoteActiveCount(res.data.active_sessions || 0); })
        .catch(() => {});
      // Fetch pending video call count
      axios.get(`${API}/video-calls/call-requests/pending`, getAuthHeader())
        .then(res => setPendingVideoCallCount((res.data.requests || []).length))
        .catch(() => {});
    }, 15000);
    // Initial remote badge fetch
    axios.get(`${API}/remote-sessions/unread-count`, getAuthHeader())
      .then(res => { setRemoteAlertCount(res.data.alert_count || 0); setRemoteActiveCount(res.data.active_sessions || 0); })
      .catch(() => {});
    // Initial video call badge fetch
    axios.get(`${API}/video-calls/call-requests/pending`, getAuthHeader())
      .then(res => setPendingVideoCallCount((res.data.requests || []).length))
      .catch(() => {});
    return () => clearInterval(pollInterval);
  }, [navigate, fetchNotifications, fetchMessageUnreadCount, fetchPayrollSettings, fetchPayrollSummary, fetchFormSubmissions]);

  const fetchData = async () => {
    try {
      const [employeesRes, entriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/employees`, getAuthHeader()),
        axios.get(`${API}/admin/time-entries`, getAuthHeader()),
        axios.get(`${API}/admin/summary`, getAuthHeader())
      ]);

      setEmployees(employeesRes.data);
      setTimeEntries(entriesRes.data);
      setSummary(summaryRes.data);
      // Update timestamp to trigger report refresh
      setLastDataUpdate(Date.now());
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
    }
  };

  // Master refresh function - refreshes all dashboard data
  const handleMasterRefresh = async () => {
    setMasterRefreshing(true);
    try {
      await Promise.all([
        fetchData(),
        fetchNotifications(),
        fetchMessageUnreadCount(),
        fetchPayrollSummary(),
        fetchPendingW9s()
      ]);
      // Update refresh key to trigger child component refreshes
      setRefreshKey(Date.now());
      // Silent refresh - no toast notification
    } catch (error) {
      console.error("Error refreshing dashboard:", error);
      toast.error("Failed to refresh some data");
    } finally {
      setMasterRefreshing(false);
    }
  };

  // Handle iOS Quick Actions (long-press shortcuts)
  // Checks for pending action in localStorage when the dashboard mounts
  useEffect(() => {
    const handlePendingShortcut = () => {
      const pendingAction = localStorage.getItem('pendingShortcutAction');
      if (!pendingAction) return;
      
      // Clear the pending action immediately to prevent re-triggering
      localStorage.removeItem('pendingShortcutAction');
      console.log('[AdminDashboard] Handling pending shortcut action:', pendingAction);
      
      // Small delay to ensure the dashboard is fully mounted
      setTimeout(() => {
        switch (pendingAction) {
          case 'StartTrip':
            // Start GPS tracking via the tracker component
            setActivePage("operations");
            setTimeout(() => {
              if (gpsTrackerRef.current?.startTrip) {
                gpsTrackerRef.current.startTrip();
                toast.info('Starting GPS trip...', { duration: 2000 });
              }
            }, 400);
            break;
            
          case 'LogMiles':
            // Open manual trip entry form
            setActivePage("operations");
            setTimeout(() => {
              if (gpsTrackerRef.current?.openManualEntry) {
                gpsTrackerRef.current.openManualEntry();
                toast.success('Manual trip entry opened', { duration: 2000 });
              }
              if (gpsTrackerRef.current?.scrollIntoView) {
                gpsTrackerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 400); // Allow time for the Operations group to expand
            break;
            
          case 'ClockIn':
            // Navigate to payroll section for clock-in (employee portal view)
            // For admin, we'll open the "Hours by Employee" section
            setShowHoursByEmployee(true);
            toast.info('Opening employee hours section...', { duration: 2000 });
            setTimeout(() => {
              const hoursSection = document.querySelector('[data-testid="hours-section"]');
              if (hoursSection) {
                hoursSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 300);
            break;
            
          default:
            console.warn('[AdminDashboard] Unknown shortcut action:', pendingAction);
        }
      }, 500);
    };
    
    // Check on mount with a delay to ensure localStorage is ready
    const mountTimer = setTimeout(handlePendingShortcut, 300);
    
    // Also check again after a longer delay in case of race conditions
    const retryTimer = setTimeout(handlePendingShortcut, 1000);
    
    // Also listen for the shortcutAction event (in case it's dispatched while on this page)
    const handleShortcutEvent = (event) => {
      const { action } = event.detail;
      console.log('[AdminDashboard] Received shortcut event:', action);
      localStorage.setItem('pendingShortcutAction', action);
      handlePendingShortcut();
    };
    
    window.addEventListener('shortcutAction', handleShortcutEvent);
    
    return () => {
      clearTimeout(mountTimer);
      clearTimeout(retryTimer);
      window.removeEventListener('shortcutAction', handleShortcutEvent);
    };
  }, []);

  // Email configuration functions
  const fetchEmailStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/email/status`, getAuthHeader());
      setEmailStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch email status:", error);
    }
  }, []);

  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error("Please enter an email address");
      return;
    }
    setSendingTestEmail(true);
    try {
      const response = await axios.post(
        `${API}/admin/email/test`,
        { email: testEmailAddress },
        getAuthHeader()
      );
      if (response.data.status === "success") {
        toast.success("Test email sent! Check your inbox.");
      } else if (response.data.status === "mocked") {
        toast.info("Test email logged to console (MOCKED mode). Add RESEND_API_KEY to send real emails.");
      } else {
        toast.error(`Failed: ${response.data.message}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Fetch form submissions when section is opened
  useEffect(() => {
    if (showFormsSection) {
      fetchFormSubmissions();
    }
  }, [showFormsSection, fetchFormSubmissions]);

  // Refresh payroll summary when section is opened
  useEffect(() => {
    if (showStatsSection) {
      fetchPayrollSummary();
    }
  }, [showStatsSection, fetchPayrollSummary]);

  // Fetch email status when email settings is opened
  useEffect(() => {
    if (showEmailSettings) {
      fetchEmailStatus();
    }
  }, [showEmailSettings, fetchEmailStatus]);

  const handleUpdateSubmissionStatus = async (formType, submissionId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const endpoint = formType === "job_applications" 
        ? "job-applications" 
        : formType === "consignment_inquiries" 
          ? "consignment-inquiries" 
          : "consignment-agreements";
      
      await axios.put(
        `${API}/admin/forms/${endpoint}/${submissionId}/status`,
        { status: newStatus },
        getAuthHeader()
      );
      
      toast.success("Status updated successfully");
      fetchFormSubmissions();
      
      // Update selected submission if viewing details
      if (selectedSubmission && selectedSubmission.id === submissionId) {
        setSelectedSubmission({ ...selectedSubmission, status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Download form submission as PDF
  const handleDownloadSubmission = async (submission) => {
    try {
      toast.loading("Preparing PDF...", { id: "download-loading" });
      
      let endpoint;
      if (submission.formType === "job_applications") {
        endpoint = "job-applications";
      } else if (submission.formType === "consignment_inquiries") {
        endpoint = "consignment-inquiries";
      } else if (submission.formType === "item_additions") {
        endpoint = "item-additions";
      } else {
        endpoint = "consignment-agreements";
      }
      
      const response = await axios.get(
        `${API}/admin/forms/${endpoint}/${submission.id}/pdf`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      toast.dismiss("download-loading");
      
      let formType;
      if (submission.formType === "job_applications") {
        formType = "Job_Application";
      } else if (submission.formType === "consignment_inquiries") {
        formType = "Consignment_Inquiry";
      } else if (submission.formType === "item_additions") {
        formType = "Item_Addition";
      } else {
        formType = "Consignment_Agreement";
      }
      const filename = `${submission.full_name.replace(/\s+/g, "_")}_${formType}.pdf`;
      
      // Create download link - same method as ReportsSection
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded");
    } catch (error) {
      toast.dismiss("download-loading");
      console.error("Download error:", error);
      if (error.response?.status === 404) {
        toast.error("Record not found - it may have been deleted");
      } else {
        toast.error("Failed to download PDF");
      }
    }
  };

  // Fetch clock status for all employees
  const fetchEmployeeClockStatuses = useCallback(async () => {
    try {
      const statuses = {};
      // Fetch clock status for each employee
      await Promise.all(
        employees.map(async (emp) => {
          try {
            const response = await axios.get(`${API}/admin/employee/${emp.id}/clock-status`, getAuthHeader());
            statuses[emp.id] = response.data.is_clocked_in;
          } catch {
            statuses[emp.id] = false;
          }
        })
      );
      setEmployeeClockStatuses(statuses);
      
      // Update admin Lock Screen widget with clocked-in employees
      const clockedInEmployees = employees
        .filter(emp => statuses[emp.id])
        .map(emp => ({
          name: emp.name || emp.email,
          clockInTime: new Date() // We don't have exact time here, using now as placeholder
        }));
      LiveActivityService.updateAdminWidget(clockedInEmployees);
    } catch (error) {
      console.error("Failed to fetch employee clock statuses:", error);
    }
  }, [employees, getAuthHeader]);

  // Fetch clock statuses when employees section is expanded OR on initial load
  useEffect(() => {
    if (employees.length > 0) {
      fetchEmployeeClockStatuses();
    }
  }, [employees.length, fetchEmployeeClockStatuses]);

  // Poll clock statuses every 30 seconds to keep the count updated
  useEffect(() => {
    if (employees.length > 0) {
      const interval = setInterval(() => {
        fetchEmployeeClockStatuses();
      }, 30000); // 30 seconds
      return () => clearInterval(interval);
    }
  }, [employees.length, fetchEmployeeClockStatuses]);

  // Refresh time entries when section is opened
  useEffect(() => {
    if (showTimeEntries) {
      fetchData();
    }
  }, [showTimeEntries]);

  // Refresh hours by employee when section is opened
  useEffect(() => {
    if (showHoursByEmployee) {
      fetchData();
    }
  }, [showHoursByEmployee]);

  const handleDeleteSubmission = async (formType, submissionId) => {
    if (!window.confirm("Are you sure you want to delete this submission? This action cannot be undone.")) {
      return;
    }
    
    try {
      let endpoint;
      switch (formType) {
        case "job_applications":
          endpoint = "job-applications";
          break;
        case "consignment_inquiries":
          endpoint = "consignment-inquiries";
          break;
        case "item_additions":
          endpoint = "item-additions";
          break;
        default:
          endpoint = "consignment-agreements";
      }
      
      await axios.delete(
        `${API}/admin/forms/${endpoint}/${submissionId}`,
        getAuthHeader()
      );
      
      toast.success("Submission deleted successfully");
      setShowSubmissionDetails(false);
      setSelectedSubmission(null);
      fetchFormSubmissions();
      if (formType === "item_additions" && fetchItemAdditions) {
        fetchItemAdditions();
      }
    } catch (error) {
      toast.error("Failed to delete submission");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { bg: "bg-blue-100", text: "text-blue-700", icon: AlertCircle },
      reviewed: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Eye },
      contacted: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle },
      archived: { bg: "bg-gray-100", text: "text-gray-700", icon: Archive }
    };
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {status || "new"}
      </span>
    );
  };

  const formatSubmissionDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Sorting function
  const handleSort = (table, key) => {
    setSortConfig(prev => ({
      ...prev,
      [table]: {
        key,
        direction: prev[table].key === key && prev[table].direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const getSortedData = (data, table) => {
    const { key, direction } = sortConfig[table];
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle nested properties
      if (key === 'user_name' || key === 'name' || key === 'full_name') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      // Handle dates
      if (key.includes('_at') || key === 'clock_in' || key === 'clock_out' || key === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      // Handle numbers
      if (typeof aVal === 'number' || key === 'hours' || key === 'total_hours' || key === 'shifts' || key === 'hourly_rate') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filter functions for search
  const getFilteredFormSubmissions = (submissions) => {
    let filtered = submissions;
    
    // Apply search filter
    if (formSearchQuery.trim()) {
      const query = formSearchQuery.toLowerCase();
      filtered = filtered.filter(item => {
        const name = (item.full_name || item.name || '').toLowerCase();
        const email = (item.email || '').toLowerCase();
        const phone = (item.phone || '').toLowerCase();
        return name.includes(query) || email.includes(query) || phone.includes(query);
      });
    }
    
    // Apply status filter
    if (formStatusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === formStatusFilter);
    }
    
    return filtered;
  };

  const SortableHeader = ({ table, sortKey, children, className = "" }) => {
    const isActive = sortConfig[table]?.key === sortKey;
    const direction = sortConfig[table]?.direction;
    return (
      <th 
        className={`cursor-pointer hover:bg-[#F9F6F7] transition-colors select-none ${className}`}
        onClick={() => handleSort(table, sortKey)}
      >
        <div className="flex items-center gap-1">
          {children}
          <span className={`transition-opacity ${isActive ? 'opacity-100' : 'opacity-30'}`}>
            {isActive && direction === 'asc' ? '↑' : '↓'}
          </span>
        </div>
      </th>
    );
  };

  // Employee portal view functions
  const handleViewEmployeePortal = async (employee) => {
    setViewingEmployee(employee);
    setShowEmployeePortal(true);
    setLoadingPortal(true);
    setEmployeeClockStatus(null);
    setShowClockConfirm(null);
    
    try {
      // Fetch employee's time entries, summary, W-9 status, W-8BEN status, and clock status
      const [entriesRes, summaryRes, w9Res, w8benRes, clockRes] = await Promise.all([
        axios.get(`${API}/admin/employee/${employee.id}/entries`, getAuthHeader()),
        axios.get(`${API}/admin/employee/${employee.id}/summary`, getAuthHeader()),
        axios.get(`${API}/admin/employees/${employee.id}/w9/status`, getAuthHeader()).catch(() => ({ data: { has_w9: false, status: 'not_submitted' } })),
        axios.get(`${API}/admin/employees/${employee.id}/w8ben/status`, getAuthHeader()).catch(() => ({ data: { has_w8ben: false, w8ben_documents: [] } })),
        axios.get(`${API}/admin/employee/${employee.id}/clock-status`, getAuthHeader()).catch(() => ({ data: { is_clocked_in: false } }))
      ]);
      
      // Fetch 1099s for this employee
      const currentYear = new Date().getFullYear();
      let employee1099s = { documents: [], count: 0 };
      try {
        const [current1099s, previous1099s] = await Promise.all([
          axios.get(`${API}/financials/my-1099s/${currentYear - 1}?user_id=${employee.id}`, getAuthHeader()),
          axios.get(`${API}/financials/my-1099s/${currentYear - 2}?user_id=${employee.id}`, getAuthHeader())
        ]);
        const allDocs = [
          ...(current1099s.data.documents || []),
          ...(previous1099s.data.documents || [])
        ];
        employee1099s = {
          documents: allDocs,
          count: allDocs.length
        };
      } catch (err) {
        console.log('No 1099s for employee:', err);
      }
      
      setEmployeePortalData({
        entries: entriesRes.data,
        summary: summaryRes.data,
        w9Status: w9Res.data,
        w8benStatus: w8benRes.data,
        my1099s: employee1099s
      });
      setEmployeeClockStatus(clockRes.data);
    } catch (error) {
      console.error("Failed to fetch employee portal data:", error);
      // Use fallback data from existing state
      const employeeEntries = timeEntries.filter(e => e.employee_id === employee.id);
      const employeeStats = summary.by_employee.find(e => e.user_id === employee.id) || {
        hours: 0,
        shifts: 0
      };
      setEmployeePortalData({
        entries: employeeEntries,
        summary: {
          total_hours: employeeStats.hours,
          total_shifts: employeeStats.shifts,
          week_hours: 0,
          period_hours: employeeStats.hours,
          period_shifts: employeeStats.shifts,
          hourly_rate: employee.hourly_rate || payrollSettings.default_hourly_rate || 20,
          estimated_pay: employeeStats.hours * (employee.hourly_rate || payrollSettings.default_hourly_rate || 20)
        },
        w9Status: { has_w9: false, status: 'not_submitted' },
        w8benStatus: { has_w8ben: false, w8ben_documents: [] },
        my1099s: { documents: [], count: 0 }
      });
      setEmployeeClockStatus({ is_clocked_in: false });
    } finally {
      setLoadingPortal(false);
    }
  };

  // View consignor portal (from admin perspective)
  const handleViewConsignorPortal = async (consignor) => {
    setViewingConsignor(consignor);
    setShowConsignorPortal(true);
    setLoadingConsignorPortal(true);
    setConsignorPortalData(null);
    
    try {
      const response = await axios.get(
        `${API}/admin/consignor/${encodeURIComponent(consignor.email)}/portal-data`,
        getAuthHeader()
      );
      setConsignorPortalData(response.data);
    } catch (error) {
      console.error("Failed to fetch consignor portal data:", error);
      toast.error("Failed to load consignor data");
    } finally {
      setLoadingConsignorPortal(false);
    }
  };

  // Admin clock in/out employee
  const handleAdminClockEmployee = async (action) => {
    if (!viewingEmployee) return;
    
    setClockingEmployee(true);
    try {
      const response = await axios.post(
        `${API}/admin/employee/${viewingEmployee.id}/clock`,
        { action },
        getAuthHeader()
      );
      
      toast.success(response.data.message);
      
      // Refresh clock status (with error handling)
      try {
        const clockRes = await axios.get(`${API}/admin/employee/${viewingEmployee.id}/clock-status`, getAuthHeader());
        setEmployeeClockStatus(clockRes.data);
      } catch (e) {
        console.error("Failed to refresh clock status:", e);
      }
      
      // Refresh employee portal data (with error handling)
      try {
        const [entriesRes, summaryRes] = await Promise.all([
          axios.get(`${API}/admin/employee/${viewingEmployee.id}/entries`, getAuthHeader()),
          axios.get(`${API}/admin/employee/${viewingEmployee.id}/summary`, getAuthHeader())
        ]);
        setEmployeePortalData(prev => ({
          ...prev,
          entries: entriesRes.data,
          summary: summaryRes.data
        }));
      } catch (e) {
        console.error("Failed to refresh portal data:", e);
      }
      
      // Also refresh main dashboard data
      fetchData();
    } catch (error) {
      console.error("Clock action failed:", error);
      toast.error(error.response?.data?.detail || `Failed to clock ${action}`);
    } finally {
      setClockingEmployee(false);
      setShowClockConfirm(null);
    }
  };

  // Timer effect for admin portal view - updates every second when employee is clocked in
  useEffect(() => {
    if (!showEmployeePortal || !employeeClockStatus?.is_clocked_in || !employeeClockStatus?.clock_in_time) {
      setPortalElapsedTime(0);
      return;
    }
    
    const updateElapsed = () => {
      const clockInTime = new Date(employeeClockStatus.clock_in_time).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - clockInTime) / 1000);
      setPortalElapsedTime(elapsed > 0 ? elapsed : 0);
    };
    
    updateElapsed(); // Initial update
    const interval = setInterval(updateElapsed, 1000);
    
    return () => clearInterval(interval);
  }, [showEmployeePortal, employeeClockStatus?.is_clocked_in, employeeClockStatus?.clock_in_time]);

  // Format seconds to HH:MM:SS for portal timer
  const formatPortalTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Employee Shifts Management Functions
  const handleViewEmployeeShifts = async (employee) => {
    setShowEmployeeShiftsModal(employee);
    setLoadingShifts(true);
    try {
      // Use the correct endpoint - /entries not /shifts
      const response = await axios.get(`${API}/admin/employee/${employee.user_id}/entries`, getAuthHeader());
      setEmployeeShifts(response.data || []);
    } catch (error) {
      console.error("Failed to fetch employee shifts:", error);
      // Fallback to filtering from timeEntries
      const shifts = timeEntries.filter(e => e.user_id === employee.user_id || e.employee_id === employee.user_id);
      setEmployeeShifts(shifts);
    } finally {
      setLoadingShifts(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/mark-read`, {}, getAuthHeader());
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark notifications as read");
    }
  };

  const handleClearNotifications = async () => {
    try {
      await axios.delete(`${API}/admin/notifications`, getAuthHeader());
      setNotifications([]);
      setUnreadCount(0);
      toast.success("All notifications cleared");
    } catch (error) {
      toast.error("Failed to clear notifications");
    }
  };

  const formatNotificationTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setAddingEmployee(true);

    try {
      // Prepare payload including role and admin_code if applicable
      const payload = {
        name: newEmployee.name,
        email: newEmployee.email,
        phone: newEmployee.phone,
        role: newEmployee.role || "employee"
      };
      
      // Include admin_code if role is admin
      if (payload.role === "admin" && newEmployee.admin_code) {
        payload.admin_code = newEmployee.admin_code;
      }
      
      const response = await axios.post(`${API}/admin/create-employee`, payload, getAuthHeader());
      const newEmployeeId = response.data.id;
      
      // Store employee data for welcome email preview
      const createdEmployeeData = {
        name: newEmployee.name,
        email: newEmployee.email,
        role: newEmployee.role || "employee"
      };
      
      // If W-9 file was attached, upload it
      if (newEmployeeW9File && newEmployeeId) {
        const formData = new FormData();
        formData.append('file', newEmployeeW9File);
        try {
          await axios.post(
            `${API}/admin/employees/${newEmployeeId}/w9`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          toast.success(`${newEmployee.role === "admin" ? "Admin" : "Employee"} ${newEmployee.name} created with W-9!`);
        } catch (w9Error) {
          toast.success(`${newEmployee.role === "admin" ? "Admin" : "Employee"} ${newEmployee.name} created! W-9 upload failed.`);
        }
      } else {
        toast.success(`${newEmployee.role === "admin" ? "Admin" : "Employee"} ${newEmployee.name} created successfully!`);
      }
      
      setNewEmployee({ name: "", email: "", phone: "", role: "employee", admin_code: "" });
      setNewEmployeeW9File(null);
      setSelectedJobApp("");
      setShowAddEmployee(false);
      fetchData();
      
      // Show welcome email preview for regular employees (not admins)
      if (createdEmployeeData.role === "employee") {
        setWelcomeEmailData(createdEmployeeData);
        setShowWelcomeEmailPreview(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create employee");
    } finally {
      setAddingEmployee(false);
    }
  };

  const handleRemoveEmployeeSubmit = async () => {
    if (!selectedEmployeeToRemove) {
      toast.error("Please select an employee");
      return;
    }
    
    const emp = employees.find(e => e.id === selectedEmployeeToRemove);
    if (!emp) return;
    
    setRemovingEmployee(true);
    try {
      await axios.delete(`${API}/admin/employees/${selectedEmployeeToRemove}`, getAuthHeader());
      toast.success(`${emp.name} has been removed`);
      setShowRemoveEmployee(false);
      setSelectedEmployeeToRemove("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete employee");
    } finally {
      setRemovingEmployee(false);
    }
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete ${employeeName}? This will also delete all their time entries.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/employees/${employeeId}`, getAuthHeader());
      toast.success(`${employeeName} has been removed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete employee");
    }
  };

  const handleViewEmployeeDetails = async (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeDetails(true);
    setLoadingEmployeeDetails(true);
    
    try {
      // Get employee's shifts
      const shiftsForEmployee = timeEntries.filter(e => e.user_id === employee.id);
      setEmployeeShifts(shiftsForEmployee);
    } catch (error) {
      console.error("Failed to load employee details:", error);
    } finally {
      setLoadingEmployeeDetails(false);
    }
  };

  const getEmployeeStats = (employeeId) => {
    const empEntries = timeEntries.filter(e => e.user_id === employeeId);
    const totalHours = empEntries.reduce((sum, e) => sum + (e.total_hours || 0), 0);
    const totalShifts = empEntries.length;
    return { totalHours: totalHours.toFixed(2), totalShifts };
  };

  // W-9 upload/download handlers
  const handleW9Upload = async (employeeId, file) => {
    if (!file) return;
    
    setUploadingW9(employeeId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/admin/employees/${employeeId}/w9`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success("W-9 uploaded successfully!");
      fetchData(); // Refresh employee list to show W-9 status
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload W-9");
    } finally {
      setUploadingW9(null);
    }
  };

  const handleW9Download = async (employeeId, employeeName) => {
    try {
      const response = await axios.get(
        `${API}/admin/employees/${employeeId}/w9`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : `w9_${employeeName.replace(/\s+/g, '_')}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download W-9");
    }
  };

  const handleW9Delete = async (employeeId) => {
    if (!window.confirm("Are you sure you want to delete this W-9 document?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/employees/${employeeId}/w9`, getAuthHeader());
      toast.success("W-9 deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete W-9");
    }
  };

  const handleDownloadBlankW9 = () => {
    // Open IRS W-9 form in new tab
    window.open("https://www.irs.gov/pub/irs-pdf/fw9.pdf", "_blank");
    toast.success("Opening W-9 form...");
  };

  // View W-9 in modal (without downloading) - Now shows list of all W-9s
  const handleViewW9 = async (employeeId, employeeName, fromPortal = false) => {
    if (!employeeId) {
      toast.error("Invalid employee ID");
      return;
    }
    
    setLoadingW9Viewer(true);
    setShowW9ViewerModal(true);
    setSelectedW9Index(0);
    setEmployeeW9List([]);
    setW9ViewerFromPortal(fromPortal);
    
    // Get employee's start date
    const employee = employees.find(e => e.id === employeeId);
    const employeeStartDate = employee?.start_date || null;
    
    try {
      // Get all W-9 documents for this employee
      const statusRes = await axios.get(`${API}/admin/employees/${employeeId}/w9/status`, getAuthHeader());
      const w9Documents = (statusRes.data.w9_documents || []).filter(doc => doc && doc.id);
      
      if (w9Documents.length === 0) {
        toast.error("No W-9 documents found");
        setShowW9ViewerModal(false);
        return;
      }
      
      // Load the first W-9 document
      const firstDoc = w9Documents[0];
      const response = await axios.get(`${API}/admin/employees/${employeeId}/w9/${firstDoc.id}`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      setEmployeeW9List(w9Documents);
      setViewingW9({
        employeeId,
        employeeName,
        employeeStartDate,
        docId: firstDoc.id,
        url,
        contentType: response.headers['content-type'],
        filename: firstDoc.filename,
        status: firstDoc.status,
        uploadedAt: firstDoc.uploaded_at
      });
    } catch (error) {
      toast.error("Failed to load W-9");
      setShowW9ViewerModal(false);
    } finally {
      setLoadingW9Viewer(false);
    }
  };

  // Open Portal W-9 Modal (dark theme - for Employee Portal view)
  const handleOpenPortalW9Modal = async (employeeId, employeeName) => {
    if (!employeeId) {
      toast.error("Invalid employee ID");
      return;
    }
    
    setShowPortalW9Modal(true);
    setLoadingPortalW9(true);
    setPortalW9Docs([]);
    
    try {
      const response = await axios.get(`${API}/admin/employees/${employeeId}/w9/status`, getAuthHeader());
      const docs = (response.data.w9_documents || []).filter(doc => doc && doc.id);
      setPortalW9Docs(docs);
    } catch (error) {
      toast.error("Failed to load W-9 documents");
    } finally {
      setLoadingPortalW9(false);
    }
  };

  // Preview W-9 from Portal Modal
  const handlePreviewPortalW9 = async (doc) => {
    if (!doc || !doc.id || !viewingEmployee?.id) return;
    
    try {
      const response = await axios.get(
        `${API}/admin/employees/${viewingEmployee.id}/w9/${doc.id}`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      setPreviewingPortalW9({
        url,
        contentType: response.headers['content-type'],
        filename: doc.filename || 'w9.pdf',
      });
    } catch (error) {
      toast.error("Failed to preview W-9");
    }
  };

  // Download W-9 from Portal Modal
  const handleDownloadPortalW9 = async (doc) => {
    if (!doc || !doc.id || !viewingEmployee?.id) return;
    
    try {
      const response = await axios.get(
        `${API}/admin/employees/${viewingEmployee.id}/w9/${doc.id}`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'w9.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("W-9 downloaded!");
    } catch (error) {
      toast.error("Failed to download W-9");
    }
  };

  // Switch to a different W-9 document
  const handleSelectW9 = async (index) => {
    if (index === selectedW9Index || !employeeW9List[index]) return;
    
    const doc = employeeW9List[index];
    if (!doc || !doc.id) {
      toast.error("Invalid document");
      return;
    }
    
    setLoadingW9Viewer(true);
    
    try {
      // Revoke old URL
      if (viewingW9?.url) {
        window.URL.revokeObjectURL(viewingW9.url);
      }
      
      const response = await axios.get(`${API}/admin/employees/${viewingW9.employeeId}/w9/${doc.id}`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      setSelectedW9Index(index);
      setViewingW9({
        ...viewingW9,
        docId: doc.id,
        url,
        contentType: response.headers['content-type'],
        filename: doc.filename,
        status: doc.status,
        uploadedAt: doc.uploaded_at
      });
    } catch (error) {
      toast.error("Failed to load W-9");
    } finally {
      setLoadingW9Viewer(false);
    }
  };

  // Delete a specific W-9 document
  const handleDeleteW9Doc = async (employeeId, docId) => {
    if (!employeeId || !docId) {
      toast.error("Invalid document or employee ID");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this W-9 document?")) return;
    
    try {
      await axios.delete(`${API}/admin/employees/${employeeId}/w9/${docId}`, getAuthHeader());
      toast.success("W-9 deleted successfully");
      
      // Remove from list
      const newList = employeeW9List.filter(doc => doc.id !== docId);
      setEmployeeW9List(newList);
      
      if (newList.length === 0) {
        closeW9Viewer();
        fetchData();
      } else {
        // Load first remaining document
        setSelectedW9Index(0);
        handleSelectW9(0);
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to delete W-9");
    }
  };

  const closeW9Viewer = () => {
    if (viewingW9?.url) {
      window.URL.revokeObjectURL(viewingW9.url);
    }
    setViewingW9(null);
    setEmployeeW9List([]);
    setSelectedW9Index(0);
    setShowW9ViewerModal(false);
  };

  // W-9 Review functions
  const fetchPendingW9s = useCallback(async () => {
    setLoadingPendingW9s(true);
    try {
      const response = await axios.get(`${API}/admin/w9/pending`, getAuthHeader());
      setPendingW9s(response.data);
    } catch (error) {
      console.error("Failed to fetch pending W-9s:", error);
    } finally {
      setLoadingPendingW9s(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (showW9ReviewSection) {
      fetchPendingW9s();
    }
  }, [showW9ReviewSection, fetchPendingW9s]);

  const handleApproveW9 = async (employeeId) => {
    try {
      await axios.post(`${API}/admin/employees/${employeeId}/w9/approve`, {}, getAuthHeader());
      toast.success("W-9 approved!");
      setReviewingW9(null);
      fetchPendingW9s();
      fetchData();
    } catch (error) {
      toast.error("Failed to approve W-9");
    }
  };

  // Approve a specific W-9 document
  const handleApproveW9Doc = async (employeeId, docId) => {
    if (!employeeId || !docId) {
      toast.error("Invalid document or employee ID");
      return;
    }
    try {
      await axios.post(`${API}/admin/employees/${employeeId}/w9/${docId}/approve`, {}, getAuthHeader());
      toast.success("W-9 approved!");
      // Refresh the W-9 list
      if (viewingW9) {
        handleViewW9(employeeId, viewingW9.employeeName);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to approve W-9");
    }
  };

  const handleRejectW9 = async (employeeId) => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    try {
      await axios.post(`${API}/admin/employees/${employeeId}/w9/reject`, { reason: rejectReason }, getAuthHeader());
      toast.success("W-9 returned for corrections");
      setReviewingW9(null);
      setRejectReason("");
      fetchPendingW9s();
      fetchData();
    } catch (error) {
      toast.error("Failed to reject W-9");
    }
  };

  // ==================== CONTRACTOR AGREEMENT REVIEW FUNCTIONS ====================
  const fetchPendingAgreements = useCallback(async () => {
    setLoadingPendingAgreements(true);
    try {
      const response = await axios.get(`${API}/contractor-agreement/admin/pending`, getAuthHeader());
      setPendingAgreements(response.data.pending || []);
    } catch (error) {
      console.error("Failed to fetch pending agreements:", error);
    } finally {
      setLoadingPendingAgreements(false);
    }
  }, []);

  const handleApproveAgreement = async (employeeId) => {
    try {
      await axios.post(`${API}/contractor-agreement/admin/employee/${employeeId}/approve`, {}, getAuthHeader());
      toast.success("Contractor Agreement approved!");
      setReviewingAgreement(null);
      fetchPendingAgreements();
      fetchData();
    } catch (error) {
      toast.error("Failed to approve agreement");
    }
  };

  const handleRejectAgreement = async (employeeId) => {
    try {
      await axios.post(`${API}/contractor-agreement/admin/employee/${employeeId}/reject`, { 
        feedback: agreementRejectReason || "Please review and sign again" 
      }, getAuthHeader());
      toast.success("Agreement returned for corrections");
      setReviewingAgreement(null);
      setAgreementRejectReason("");
      fetchPendingAgreements();
      fetchData();
    } catch (error) {
      toast.error("Failed to reject agreement");
    }
  };

  // ==================== W-8BEN REVIEW FUNCTIONS ====================
  const fetchPendingW8bens = useCallback(async () => {
    setLoadingPendingW8bens(true);
    try {
      const response = await axios.get(`${API}/admin/w8ben/pending`, getAuthHeader());
      setPendingW8bens(response.data.pending || []);
    } catch (error) {
      console.error("Failed to fetch pending W-8BENs:", error);
    } finally {
      setLoadingPendingW8bens(false);
    }
  }, []);

  const handleApproveW8ben = async (employeeId, docId) => {
    try {
      await axios.post(`${API}/admin/employees/${employeeId}/w8ben/${docId}/approve`, {}, getAuthHeader());
      toast.success("W-8BEN approved!");
      setReviewingW8ben(null);
      fetchPendingW8bens();
      fetchData();
    } catch (error) {
      toast.error("Failed to approve W-8BEN");
    }
  };

  const handleRejectW8ben = async (employeeId, docId) => {
    try {
      await axios.post(`${API}/admin/employees/${employeeId}/w8ben/${docId}/reject`, { 
        feedback: w8benRejectReason || "Please review and re-submit" 
      }, getAuthHeader());
      toast.success("W-8BEN returned for corrections");
      setReviewingW8ben(null);
      setW8benRejectReason("");
      fetchPendingW8bens();
      fetchData();
    } catch (error) {
      toast.error("Failed to reject W-8BEN");
    }
  };

  // Edit time entry handlers
  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    // Convert ISO strings to local datetime-local input format
    const formatForInput = (isoString) => {
      if (!isoString) return "";
      const date = new Date(isoString);
      // Format as local datetime for datetime-local input (YYYY-MM-DDTHH:mm)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    setEditFormData({
      clock_in: formatForInput(entry.clock_in),
      clock_out: formatForInput(entry.clock_out),
      total_hours: entry.total_hours?.toString() || ""
    });
    setShowEditEntry(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);

    try {
      const updateData = {};
      if (editFormData.clock_in) {
        updateData.clock_in = new Date(editFormData.clock_in).toISOString();
      }
      if (editFormData.clock_out) {
        updateData.clock_out = new Date(editFormData.clock_out).toISOString();
      }

      await axios.put(
        `${API}/admin/time-entries/${editingEntry.id}`,
        updateData,
        getAuthHeader()
      );
      
      toast.success("Time entry updated successfully!");
      setShowEditEntry(false);
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update time entry");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this time entry?")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/time-entries/${entryId}`, getAuthHeader());
      toast.success("Time entry deleted");
      fetchData();
      fetchPayrollSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete time entry");
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    setAddingEntry(true);

    try {
      const entryPayload = {
        employee_id: newEntryData.employee_id,
        clock_in: new Date(newEntryData.clock_in).toISOString()
      };
      
      if (newEntryData.clock_out) {
        entryPayload.clock_out = new Date(newEntryData.clock_out).toISOString();
      }

      await axios.post(`${API}/admin/time-entries`, entryPayload, getAuthHeader());
      
      toast.success("Time entry created successfully!");
      setShowAddEntry(false);
      setNewEntryData({ employee_id: "", clock_in: "", clock_out: "" });
      fetchData();
      fetchPayrollSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create time entry");
    } finally {
      setAddingEntry(false);
    }
  };

  // Payroll handlers
  const handleGeneratePayrollReport = async () => {
    setPayrollLoading(true);
    try {
      const payload = {
        period_type: payrollFilters.period_type,
        period_index: parseInt(payrollFilters.period_index) || 0,
        hourly_rate: parseFloat(payrollFilters.hourly_rate) || null,
        employee_id: payrollFilters.employee_id || null
      };
      
      if (payrollFilters.period_type === "custom") {
        payload.start_date = payrollFilters.custom_start;
        payload.end_date = payrollFilters.custom_end;
      }

      const response = await axios.post(`${API}/admin/payroll/report`, payload, getAuthHeader());
      setPayrollReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payroll report");
    } finally {
      setPayrollLoading(false);
    }
  };

  const handleDownloadPayrollPDF = async () => {
    try {
      const payload = {
        period_type: payrollFilters.period_type,
        period_index: parseInt(payrollFilters.period_index) || 0,
        hourly_rate: parseFloat(payrollFilters.hourly_rate) || null,
        employee_id: payrollFilters.employee_id || null
      };
      
      if (payrollFilters.period_type === "custom") {
        payload.start_date = payrollFilters.custom_start;
        payload.end_date = payrollFilters.custom_end;
      }

      const response = await axios.post(`${API}/admin/payroll/report/pdf`, payload, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleSavePayrollSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/payroll/settings`, {
        id: "payroll_settings",
        pay_period_start_date: payrollSettings.pay_period_start_date,
        default_hourly_rate: parseFloat(payrollSettings.default_hourly_rate) || 15.00
      }, getAuthHeader());
      
      toast.success("Payroll settings saved!");
      setShowPayrollSettings(false);
      fetchPayrollSummary(); // Refresh the summary with new period
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Quick save for inline pay period update
  const handleUpdatePayrollSettings = async () => {
    try {
      await axios.put(`${API}/admin/payroll/settings`, {
        id: "payroll_settings",
        pay_period_start_date: payrollSettings.pay_period_start_date,
        default_hourly_rate: parseFloat(payrollSettings.default_hourly_rate) || 15.00
      }, getAuthHeader());
      
      toast.success("Pay period updated for all employees!");
      fetchPayrollSummary(); // Refresh the summary with new period
    } catch (error) {
      toast.error("Failed to update pay period");
    }
  };

  const getPeriodLabel = (periodType, periodIndex) => {
    const index = parseInt(periodIndex);
    if (periodType === "biweekly") {
      if (index === 0) return "Current Pay Period";
      if (index === -1) return "Last Pay Period";
      if (index === -2) return "2 Periods Ago";
      return `${Math.abs(index)} Periods ${index > 0 ? 'From Now' : 'Ago'}`;
    }
    if (periodType === "monthly") {
      const date = new Date();
      date.setMonth(date.getMonth() + index);
      return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    }
    if (periodType === "yearly") {
      return (new Date().getFullYear() + index).toString();
    }
    return "Custom Period";
  };

  const handleUpdateEmployeeRate = async (employeeId) => {
    const rate = parseFloat(editingRateValue);
    if (isNaN(rate) || rate < 0) {
      toast.error("Please enter a valid hourly rate");
      return;
    }
    
    try {
      await axios.put(`${API}/admin/employees/${employeeId}/rate`, {
        hourly_rate: rate
      }, getAuthHeader());
      
      toast.success("Hourly rate updated!");
      setEditingRateId(null);
      setEditingRateValue("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update rate");
    }
  };

  // Edit employee handlers
  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      const dateRange = getReportDateRange();
      const response = await axios.post(`${API}/admin/reports`, {
        start_date: dateRange.start,
        end_date: dateRange.end,
        employee_id: reportFilters.employee_id || null
      }, getAuthHeader());
      setReportData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadShiftReportPDF = async () => {
    if (!reportData) return;
    
    try {
      const response = await axios.post(`${API}/admin/reports/pdf`, {
        start_date: reportData.period.start,
        end_date: reportData.period.end
      }, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `shift_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Shift report PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download shift report PDF");
    }
  };

  const handleLogout = async () => {
    lightTap(); // Light haptic for navigation
    
    // Deactivate push token on logout to stop receiving notifications
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (currentUser.id) {
        await axios.post(`${API}/live-activity/deactivate-device-token`, {
          user_id: currentUser.id,
          user_type: currentUser.is_admin ? "admin" : "employee"
        });
      }
    } catch (e) {
      console.log("Failed to deactivate push token:", e);
    }
    
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Set flag to prevent auto Face ID on login page
    sessionStorage.setItem("justLoggedOut", "true");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' });
    } catch {
      return 'N/A';
    }
  };

  if (!user) return null;

  return (
    <div className="dashboard-container" data-testid="admin-dashboard">
      {/* Header - Sticky */}
      <header 
        className="dashboard-header sticky top-0 z-40" 
        style={{ 
          background: 'linear-gradient(90deg, #1A1A2E 0%, #16213E 100%)',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)'
        }}
      >
        {/* Left side: Admin info + Refresh + Notifications */}
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 sm:w-11 sm:h-11 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] rounded-full flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="min-w-0 hidden sm:block">
            <p className="font-semibold text-white text-sm sm:text-base truncate" data-testid="admin-name">{currentAdminName}</p>
          </div>
          
          {/* Refresh Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="p-2 sm:px-3 sm:py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300"
            onClick={() => {
              lightTap();
              handleMasterRefresh();
            }}
            disabled={masterRefreshing}
            data-testid="refresh-button"
            title="Refresh dashboard"
          >
            <RefreshCw className={`w-5 h-5 sm:w-5 sm:h-5 ${masterRefreshing ? 'animate-spin' : ''}`} />
          </Button>

          {/* Payroll Quick View */}
          <div className="relative" ref={payrollQuickViewRef}>
            <Button 
              variant="ghost" 
              size="sm" 
              className="relative p-2 sm:px-3 sm:py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300"
              onClick={() => {
                lightTap();
                if (!showPayrollQuickView) {
                  setPayrollPeriodIndex(0); // Reset to current period
                  fetchPayrollSummary(0); // Refresh data when opening
                }
                setShowPayrollQuickView(!showPayrollQuickView);
              }}
              data-testid="payroll-quick-view-btn"
            >
              <DollarSign className="w-5 h-5 sm:w-5 sm:h-5 text-[#10B981]" />
              <span className="ml-1 text-sm font-bold text-[#10B981] hidden sm:inline">
                ${payrollSummary.current_period?.amount?.toFixed(0) || '0'}
              </span>
            </Button>
            
            {/* Payroll Dropdown */}
            <AnimatePresence>
              {showPayrollQuickView && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="fixed md:absolute left-1 right-1 md:left-auto md:right-0 top-[120px] md:top-full md:mt-2 w-auto md:w-80 rounded-2xl shadow-2xl border border-[#eee] z-50 overflow-hidden"
                  style={{ backgroundColor: '#ffffff', color: '#000000' }}
                  data-testid="payroll-quick-view-dropdown"
                  onTouchStart={(e) => {
                    touchStartX.current = e.touches[0].clientX;
                    touchStartY.current = e.touches[0].clientY;
                  }}
                  onTouchEnd={(e) => {
                    if (touchStartX.current === null) return;
                    const touchEndX = e.changedTouches[0].clientX;
                    const touchEndY = e.changedTouches[0].clientY;
                    const deltaX = touchEndX - touchStartX.current;
                    const deltaY = touchEndY - touchStartY.current;
                    
                    // Only trigger if horizontal swipe is more significant than vertical
                    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
                      if (deltaX > 0) {
                        // Swipe right - go to previous period (past)
                        if (payrollPeriodIndex > -2) {
                          const newIndex = payrollPeriodIndex - 1;
                          setPayrollPeriodIndex(newIndex);
                          fetchPayrollSummary(newIndex);
                        }
                      } else {
                        // Swipe left - go to next (more current) period
                        if (payrollPeriodIndex < 0) {
                          const newIndex = payrollPeriodIndex + 1;
                          setPayrollPeriodIndex(newIndex);
                          fetchPayrollSummary(newIndex);
                        }
                      }
                    }
                    touchStartX.current = null;
                    touchStartY.current = null;
                  }}
                  onWheel={(e) => {
                    // Mac trackpad two-finger horizontal scroll with debounce
                    const now = Date.now();
                    if (now - lastWheelTime.current < 300) return; // 300ms debounce
                    
                    if (Math.abs(e.deltaX) > 30 && Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                      e.preventDefault();
                      lastWheelTime.current = now;
                      
                      if (e.deltaX < 0) {
                        // Scroll left = go to previous period (past)
                        if (payrollPeriodIndex > -2) {
                          const newIndex = payrollPeriodIndex - 1;
                          setPayrollPeriodIndex(newIndex);
                          fetchPayrollSummary(newIndex);
                        }
                      } else {
                        // Scroll right = go to next period (toward current)
                        if (payrollPeriodIndex < 0) {
                          const newIndex = payrollPeriodIndex + 1;
                          setPayrollPeriodIndex(newIndex);
                          fetchPayrollSummary(newIndex);
                        }
                      }
                    }
                  }}
                >
                  {/* Header */}
                  <div className={`p-4 ${payrollPeriodIndex === 0 ? 'bg-gradient-to-r from-[#10B981] to-[#059669]' : 'bg-gradient-to-r from-[#6366F1] to-[#4F46E5]'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-white" />
                        <h3 className="font-semibold text-white">
                          {payrollPeriodIndex === 0 ? 'Current Period' : payrollPeriodIndex === -1 ? 'Previous Period' : `${Math.abs(payrollPeriodIndex)} Periods Ago`}
                        </h3>
                      </div>
                      <button
                        onClick={() => setShowPayrollQuickView(false)}
                        className="p-1 text-white/60 hover:text-white rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Period navigation hint */}
                    <div className="flex items-center justify-between mt-1">
                      <div className="text-white/80 text-xs">
                        {payrollSummary.current_period?.start && payrollSummary.current_period?.end ? (
                          <>
                            {formatPeriodDay(payrollSummary.current_period.start)}
                            {' - '}
                            {formatPeriodDay(payrollSummary.current_period.end)}
                          </>
                        ) : 'Loading...'}
                      </div>
                      <div className="flex items-center gap-1 text-white/50 text-xs">
                        <ChevronLeft className="w-3 h-3" />
                        <span>Swipe</span>
                        <ChevronRight className="w-3 h-3" />
                      </div>
                    </div>
                    
                    <div className="mt-3 text-3xl font-bold text-white">
                      ${payrollSummary.current_period?.amount?.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-white/70 text-sm">
                      {formatHoursToHMS(payrollSummary.current_period?.hours || 0)} total
                    </div>
                    
                    {/* Period dots indicator */}
                    <div className="flex justify-center gap-1.5 mt-3">
                      {[-2, -1, 0].map((idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setPayrollPeriodIndex(idx);
                            fetchPayrollSummary(idx);
                          }}
                          className={`w-2 h-2 rounded-full transition-all ${
                            payrollPeriodIndex === idx 
                              ? 'bg-white w-4' 
                              : 'bg-white/40 hover:bg-white/60'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Employee Breakdown */}
                  <div className="p-4 max-h-[300px] overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
                    <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#374151' }}>By Employee</h4>
                    {(!payrollSummary.current_period?.by_employee || payrollSummary.current_period.by_employee.length === 0) ? (
                      <p className="text-sm text-center py-4" style={{ color: '#6B7280' }}>
                        No hours logged this period.<br/>
                        <span className="text-xs">Tap "View Full Payroll Details" for more info.</span>
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {payrollSummary.current_period.by_employee.map(emp => (
                          <div 
                            key={emp.user_id} 
                            className="flex items-center justify-between p-3 rounded-lg"
                            style={{ backgroundColor: '#F3F4F6', color: '#000000' }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                                {emp.name?.charAt(0) || '?'}
                              </div>
                              <div>
                                <p className="text-sm font-semibold" style={{ color: '#111827' }}>{emp.name}</p>
                                {emp.rate_breakdown && emp.rate_breakdown.length > 0 ? (
                                  <div className="text-xs" style={{ color: '#4B5563' }}>
                                    {emp.rate_breakdown.map((rb, i) => (
                                      <span key={i}>
                                        {i > 0 && ' + '}
                                        {formatHoursToHMS(rb.hours)} @ ${rb.rate.toFixed(2)}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs" style={{ color: '#4B5563' }}>{formatHoursToHMS(emp.hours || 0)} × ${emp.hourly_rate}/hr</p>
                                )}
                              </div>
                            </div>
                            <span className="text-base font-bold" style={{ color: '#059669' }}>${emp.amount?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Footer */}
                  <div className="p-3 border-t" style={{ borderColor: '#E5E7EB', backgroundColor: '#F9FAFB' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full hover:bg-emerald-50"
                      style={{ color: '#059669' }}
                      onClick={() => {
                        setShowPayrollQuickView(false);
                        setPayrollPeriodIndex(0);
                        setActivePage("payroll");
                        setTimeout(() => {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }, 200);
                      }}
                    >
                      View Full Payroll Details →
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Notification Bell */}
          <div className="relative" ref={notificationRef}>
            <Button 
              variant="ghost" 
              size="sm" 
              className={`relative p-2 sm:px-3 sm:py-2 transition-all duration-300 ${
                unreadCount > 0 
                  ? 'bg-[#FF1493]/20 border-2 border-[#FF1493] text-white hover:bg-[#FF1493]/30 shadow-lg shadow-[#FF1493]/30 animate-pulse' 
                  : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
              }`}
              onClick={() => {
                lightTap();
                setShowNotifications(!showNotifications);
              }}
              data-testid="notification-bell"
            >
              <Bell className={`w-5 h-5 sm:w-5 sm:h-5 ${unreadCount > 0 ? 'text-[#FF1493]' : ''}`} />
              <span className="ml-1.5 text-sm font-medium hidden sm:inline">Alerts</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF1493] text-white text-[10px] rounded-full flex items-center justify-center font-bold shadow-lg shadow-[#FF1493]/50" data-testid="notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
            
            {/* Notification Dropdown */}
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="fixed md:absolute left-1 right-1 md:left-0 md:right-auto top-[120px] md:top-full md:mt-2 w-auto md:w-96 max-w-[400px] bg-white rounded-2xl shadow-2xl border border-[#eee] z-50 overflow-hidden"
                  data-testid="notification-dropdown"
                >
                  {/* Header */}
                  <div className="p-4 bg-gradient-to-r from-[#1A1A2E] to-[#16213E]">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Bell className="w-5 h-5 text-[#00D4FF]" />
                        <h3 className="font-semibold text-white">Notifications</h3>
                        {unreadCount > 0 && (
                          <span className="px-2 py-0.5 bg-[#FF1493] text-white text-xs rounded-full font-medium">
                            {unreadCount} new
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setShowNotifications(false)}
                        className="p-1 text-white/60 hover:text-white rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Clear notifications button */}
                    {notifications.length > 0 && (
                      <Button 
                        variant="outline"
                        size="sm" 
                        className="w-full h-8 text-xs bg-red-500/20 border-red-400/30 text-red-300 hover:bg-red-500/30"
                        onClick={handleClearNotifications}
                        data-testid="clear-notifications-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Clear notifications
                      </Button>
                    )}
                  </div>

                  {/* Notification List */}
                  <div className="max-h-96 overflow-y-auto bg-white">
                    {/* Web Push Settings */}
                    <div className="p-3 border-b border-[#f0f0f0]">
                      <WebPushSettings />
                    </div>
                    
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-[#F9F6F7] rounded-full flex items-center justify-center mx-auto mb-4">
                          <Bell className="w-8 h-8 text-[#ccc]" />
                        </div>
                        <p className="text-[#888] text-sm font-medium">No notifications</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-[#f0f0f0]">
                        {notifications.map((notification) => (
                          <div 
                            key={notification.id} 
                            className={`notification-list-item p-4 hover:bg-[#faf9f7] transition-all cursor-pointer ${
                              !notification.read ? 'bg-[#FFF5F8] border-l-4 border-l-[#FF1493]' : 'bg-white'
                            }`}
                            onClick={() => handleNotificationClick(notification)}
                            data-testid={`notification-item-${notification.id}`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Icon */}
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                notification.type === 'clock_in' 
                                  ? 'bg-green-500' 
                                  : notification.type === 'clock_out'
                                  ? 'bg-red-500'
                                  : notification.type === 'w9_submission' || notification.type === 'w9_submitted'
                                  ? 'bg-orange-500'
                                  : notification.type === 'new_message'
                                  ? 'bg-pink-500'
                                  : notification.type === 'employee_message'
                                  ? 'bg-green-500'
                                  : notification.type === 'consignor_message'
                                  ? 'bg-amber-500'
                                  : notification.type === 'job_application'
                                  ? 'bg-purple-500'
                                  : notification.type === 'consignment_inquiry'
                                  ? 'bg-cyan-500'
                                  : notification.type === 'consignment_agreement'
                                  ? 'bg-emerald-500'
                                  : notification.type === 'payment_method_change'
                                  ? 'bg-amber-500'
                                  : notification.type === 'consignment_items_added'
                                  ? 'bg-teal-500'
                                  : notification.type === 'applicant_test_submission'
                                  ? 'bg-violet-500'
                                  : 'bg-blue-500'
                              }`}>
                                {notification.type === 'clock_in' 
                                  ? <LogIn className="w-5 h-5 text-white" />
                                  : notification.type === 'clock_out'
                                  ? <LogOut className="w-5 h-5 text-white" />
                                  : notification.type === 'w9_submission' || notification.type === 'w9_submitted'
                                  ? <FileText className="w-5 h-5 text-white" />
                                  : notification.type === 'new_message'
                                  ? <MessageSquare className="w-5 h-5 text-white" />
                                  : notification.type === 'employee_message' || notification.type === 'consignor_message'
                                  ? <MessageSquare className="w-5 h-5 text-white" />
                                  : notification.type === 'job_application'
                                  ? <Briefcase className="w-5 h-5 text-white" />
                                  : notification.type === 'consignment_inquiry'
                                  ? <Package className="w-5 h-5 text-white" />
                                  : notification.type === 'consignment_agreement'
                                  ? <FileSignature className="w-5 h-5 text-white" />
                                  : notification.type === 'payment_method_change'
                                  ? <CreditCard className="w-5 h-5 text-white" />
                                  : notification.type === 'consignment_items_added'
                                  ? <Package className="w-5 h-5 text-white" />
                                  : notification.type === 'applicant_test_submission'
                                  ? <ClipboardCheck className="w-5 h-5 text-white" />
                                  : <Bell className="w-5 h-5 text-white" />
                                }
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                                    notification.type === 'clock_in'
                                      ? 'bg-green-100 text-green-800'
                                      : notification.type === 'clock_out'
                                      ? 'bg-red-100 text-red-800'
                                      : notification.type === 'w9_submission' || notification.type === 'w9_submitted'
                                      ? 'bg-orange-100 text-orange-800'
                                      : notification.type === 'new_message'
                                      ? 'bg-pink-100 text-pink-800'
                                      : notification.type === 'job_application'
                                      ? 'bg-purple-100 text-purple-800'
                                      : notification.type === 'consignment_inquiry'
                                      ? 'bg-cyan-100 text-cyan-800'
                                      : notification.type === 'consignment_agreement'
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : notification.type === 'payment_method_change'
                                      ? 'bg-amber-100 text-amber-800'
                                      : notification.type === 'consignment_items_added'
                                      ? 'bg-teal-100 text-teal-800'
                                      : notification.type === 'applicant_test_submission'
                                      ? 'bg-violet-100 text-violet-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {notification.type === 'clock_in' ? 'IN' 
                                      : notification.type === 'clock_out' ? 'OUT'
                                      : notification.type === 'w9_submission' || notification.type === 'w9_submitted' ? 'W-9'
                                      : notification.type === 'new_message' ? 'MSG'
                                      : notification.type === 'job_application' ? 'JOB'
                                      : notification.type === 'consignment_inquiry' ? 'INQ'
                                      : notification.type === 'consignment_agreement' ? 'AGR'
                                      : notification.type === 'payment_method_change' ? 'PAY'
                                      : notification.type === 'consignment_items_added' ? 'ITEM'
                                      : notification.type === 'applicant_test_submission' ? 'TEST'
                                      : 'INFO'}
                                  </span>
                                  <span className="notification-time text-xs">
                                    {formatNotificationTime(notification.created_at)}
                                  </span>
                                  {!notification.read && (
                                    <span className="w-2 h-2 bg-[#FF1493] rounded-full"></span>
                                  )}
                                </div>
                                
                                <p className="notification-message text-sm leading-snug" style={{ color: '#1A1A2E', fontWeight: 500, opacity: 1 }}>
                                  {notification.message}
                                </p>
                                
                                {notification.details && (notification.details.today_hours !== undefined || notification.details.week_hours !== undefined) && (
                                  <div className="flex items-center gap-2 mt-2">
                                    {notification.details.today_hours !== undefined && (
                                      <span className="notification-meta inline-flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                                        <Clock className="w-3 h-3" />
                                        Today: {notification.details.today_hours}h
                                      </span>
                                    )}
                                    {notification.details.week_hours !== undefined && (
                                      <span className="notification-meta inline-flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                                        <Calendar className="w-3 h-3" />
                                        Week: {notification.details.week_hours}h
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {notifications.length > 0 && (
                    <div className="p-3 bg-[#F9F6F7] border-t border-[#eee] text-center">
                      <p className="text-xs text-[#888]">
                        Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right side: Navigation icons */}
        <div className="flex items-center gap-0.5 sm:gap-2">
          {/* Remote Sessions - opens full review page */}
          <Button
            variant="ghost"
            size="sm"
            className="text-white/70 hover:text-white hover:bg-white/10 p-2 sm:px-3 sm:py-2 relative"
            onClick={() => { lightTap(); navigate("/remote-sessions"); }}
            data-testid="remote-sessions-header-btn"
            title="Remote Sessions"
          >
            <Monitor className="w-5 h-5 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Remote Sessions</span>
            {(remoteAlertCount > 0 || remoteActiveCount > 0) && (
              <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold ${
                remoteAlertCount > 0 ? "bg-red-500 text-white animate-pulse" : "bg-emerald-500 text-white"
              }`} data-testid="remote-sessions-badge">
                {remoteAlertCount > 0 ? remoteAlertCount : remoteActiveCount}
              </span>
            )}
          </Button>
          {/* Video Calls */}
          <Button
            variant="ghost"
            size="sm"
            className="text-white/70 hover:text-white hover:bg-white/10 p-2 sm:px-3 sm:py-2 relative"
            onClick={() => { lightTap(); navigate("/video-calls"); }}
            data-testid="video-calls-header-btn"
            title="Video Calls"
          >
            <Video className="w-5 h-5 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Calls</span>
            {pendingVideoCallCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold bg-green-500 text-white animate-pulse" data-testid="video-calls-badge">
                {pendingVideoCallCount}
              </span>
            )}
          </Button>
          {/* Messages Shortcut */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              lightTap();
              setShowFullScreenMessages(true);
            }}
            className="text-white/70 hover:text-white hover:bg-white/10 relative p-2 sm:px-3 sm:py-2"
            data-testid="admin-messages-shortcut-btn"
            title="Messages"
          >
            <MessageSquare className="w-5 h-5 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Messages</span>
            {adminUnreadMessageCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                {adminUnreadMessageCount > 9 ? '9+' : adminUnreadMessageCount}
              </span>
            )}
          </Button>
          {/* More dropdown: Home, Logout */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-white/70 hover:text-white hover:bg-white/10 p-2 sm:px-3 sm:py-2"
                data-testid="more-menu-btn"
                title="More"
              >
                <MoreVertical className="w-5 h-5 sm:w-4 sm:h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onClick={() => { lightTap(); navigate("/"); }}
                data-testid="home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => { lightTap(); handleLogout(); }}
                className="text-red-600 focus:text-red-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="dashboard-content min-h-screen">
        {/* Bold Gradient Accent Bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#00D4FF] via-[#8B5CF6] to-[#FF1493] -mx-8 -mt-8 mb-4 rounded-b-lg" />
        
        {/* Compact Live Employee Tracker Bar */}
        <CompactEmployeeTracker 
          employees={employees}
          employeeClockStatuses={employeeClockStatuses}
          getAuthHeader={getAuthHeader}
          forceOpen={forceOpenClockedInTracker}
        />
        
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row items-end sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h1 
                className="font-poppins text-xl sm:text-2xl md:text-3xl font-bold text-white w-full sm:w-auto text-left cursor-default select-none"
                onClick={() => {
                  titleClickCount.current += 1;
                  if (titleClickTimer.current) clearTimeout(titleClickTimer.current);
                  titleClickTimer.current = setTimeout(() => {
                    titleClickCount.current = 0;
                  }, 500);
                  if (titleClickCount.current >= 3) {
                    titleClickCount.current = 0;
                    setShowEmailSettings(true);
                  }
                }}
              >
                Admin Dashboard
              </h1>
            </div>
            <div className="flex gap-2 items-start">
              {/* Employee Management + Start Trip */}
              <div className="flex flex-col gap-1">
                {/* Add, Edit, Remove row */}
                <div className="flex gap-1">
                  <Button 
                    onClick={() => {
                      buttonPress();
                      setShowAddEmployee(true);
                    }}
                    size="sm"
                    className="flex items-center gap-1 bg-gradient-to-r from-[#FF1493] to-[#E91E8C] text-white font-semibold shadow-md hover:shadow-lg hover:shadow-[#FF1493]/30 transition-all border-0 text-xs sm:text-sm h-9 flex-1"
                    data-testid="add-employee-btn"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add Employee</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                  <Button 
                    onClick={() => {
                      buttonPress();
                      setShowEditEmployee(true);
                    }}
                    size="sm"
                    className="flex items-center gap-1 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white font-semibold shadow-md hover:shadow-lg hover:shadow-[#8B5CF6]/30 transition-all border-0 text-xs sm:text-sm h-9 flex-1"
                    data-testid="edit-employee-btn"
                  >
                    <UserCog className="w-4 h-4" />
                    <span className="hidden sm:inline">Edit Employee</span>
                    <span className="sm:hidden">Edit</span>
                  </Button>
                  <Button 
                    onClick={() => {
                      buttonPress();
                      setShowRemoveEmployee(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1 border-red-400/50 text-red-400 font-semibold hover:bg-red-500/10 transition-all text-xs sm:text-sm h-9 flex-1"
                    data-testid="remove-employee-btn"
                  >
                    <UserMinus className="w-4 h-4" />
                    <span className="hidden sm:inline">Remove</span>
                  </Button>
                </div>
                {/* GPS Trip Controls Row - Unified quick trip */}
                <div className="flex gap-1 items-center">
                  {tripState.active ? (
                    <>
                      {/* Pause or Resume */}
                      {tripState.paused ? (
                        <Button
                          onClick={() => {
                            buttonPress();
                            setActivePage("operations");
                            setTimeout(() => gpsTrackerRef.current?.resumeTrip(), 400);
                          }}
                          size="sm"
                          className="flex items-center gap-1 bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-semibold shadow-md transition-all border-0 text-xs sm:text-sm h-9 flex-1"
                          data-testid="resume-trip-header-btn"
                        >
                          <Play className="w-4 h-4" />
                          <span className="hidden sm:inline">Resume</span>
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            buttonPress();
                            setActivePage("operations");
                            setTimeout(() => gpsTrackerRef.current?.pauseTrip(), 400);
                          }}
                          size="sm"
                          className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-md transition-all border-0 text-xs sm:text-sm h-9 flex-1"
                          data-testid="pause-trip-header-btn"
                        >
                          <Pause className="w-4 h-4" />
                          <span className="hidden sm:inline">Pause</span>
                        </Button>
                      )}
                      {/* End Trip */}
                      <Button
                        onClick={() => {
                          buttonPress();
                          setActivePage("operations");
                          setTimeout(() => {
                            gpsTrackerRef.current?.endTrip();
                            setTimeout(() => {
                              gpsTrackerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 500);
                          }, 400);
                        }}
                        size="sm"
                        className="flex items-center gap-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold shadow-md transition-all border-0 text-xs sm:text-sm h-9 flex-1"
                        data-testid="end-trip-header-btn"
                      >
                        <Square className="w-4 h-4" />
                        <span className="hidden sm:inline">End</span>
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => {
                        buttonPress();
                        // Force open Operations group so GPS tracker mounts
                        setActivePage("operations");
                        // Wait for mount, then start trip
                        setTimeout(() => {
                          if (gpsTrackerRef.current?.startTrip) {
                            gpsTrackerRef.current.startTrip();
                          }
                        }, 400);
                      }}
                      disabled={tripStarting}
                      size="sm"
                      className="flex items-center gap-1 bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-semibold shadow-md hover:shadow-lg hover:shadow-[#10B981]/30 transition-all border-0 text-xs sm:text-sm h-9 flex-1"
                      data-testid="start-trip-header-btn"
                    >
                      <Navigation className="w-4 h-4" />
                      {tripStarting ? "Starting..." : (
                        <>
                          <span className="hidden sm:inline">Start Trip</span>
                          <span className="sm:hidden">Trip</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Add Employee Modal */}
          <AddEmployeeModal
            show={showAddEmployee}
            onClose={() => setShowAddEmployee(false)}
            newEmployee={newEmployee}
            setNewEmployee={setNewEmployee}
            newEmployeeW9File={newEmployeeW9File}
            setNewEmployeeW9File={setNewEmployeeW9File}
            selectedJobApp={selectedJobApp}
            setSelectedJobApp={setSelectedJobApp}
            formSubmissions={formSubmissions}
            handleAddEmployee={handleAddEmployee}
            handleDownloadBlankW9={handleDownloadBlankW9}
            addingEmployee={addingEmployee}
            isOwner={isOwner}
          />

          {/* Remove Employee Modal */}
          <RemoveEmployeeModal
            show={showRemoveEmployee}
            onClose={() => setShowRemoveEmployee(false)}
            employees={employees.filter(e => e.role !== 'admin')}
            employeeToRemove={selectedEmployeeToRemove}
            setEmployeeToRemove={setSelectedEmployeeToRemove}
            handleRemoveEmployee={handleRemoveEmployeeSubmit}
            removingEmployee={removingEmployee}
          />

          {/* Welcome Email Preview Modal */}
          {showWelcomeEmailPreview && welcomeEmailData && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
              onClick={() => setShowWelcomeEmailPreview(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                className="bg-white rounded-2xl overflow-hidden w-full max-w-2xl shadow-2xl my-4"
                onClick={(e) => e.stopPropagation()}
                data-testid="welcome-email-preview-modal"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Welcome Email Sent!</h2>
                      <p className="text-green-100 text-sm">An onboarding email was sent to {welcomeEmailData.email}</p>
                    </div>
                  </div>
                </div>
                
                {/* Email Preview */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                  <div className="border rounded-xl overflow-hidden">
                    {/* Email Header */}
                    <div className="bg-gray-50 p-4 border-b">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <span className="font-medium">To:</span>
                        <span>{welcomeEmailData.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="font-medium">Subject:</span>
                        <span>Welcome to Thrifty Curator™ - Employee Portal Access & W9 Instructions</span>
                      </div>
                    </div>
                    
                    {/* Email Body Preview */}
                    <div className="p-6 bg-white">
                      <p className="text-gray-700 mb-4">
                        Hi <strong>{welcomeEmailData.name}</strong>,
                      </p>
                      
                      <p className="text-gray-700 mb-4">
                        Welcome to the <strong>Thrifty Curator™</strong> team! We're excited to have you on board.
                      </p>
                      
                      <p className="text-gray-700 mb-4">
                        This email contains important information about accessing your employee portal and completing your onboarding paperwork.
                      </p>
                      
                      {/* Portal Access Section */}
                      <div className="bg-gradient-to-r from-violet-500 to-cyan-500 rounded-xl p-5 mb-4 text-white">
                        <h3 className="font-bold mb-2 flex items-center gap-2">
                          📱 Accessing the Employee Portal
                        </h3>
                        <p className="text-sm mb-3 text-white/90">
                          The employee portal is where you'll clock in/out, view your hours, and manage your account.
                        </p>
                        <ol className="text-sm space-y-1 list-decimal list-inside text-white/90">
                          <li>Go to <span className="font-semibold">https://thrifty-curator.com/login</span></li>
                          <li>Enter your email address: <strong>{welcomeEmailData.email}</strong></li>
                          <li>Click "Find My Account"</li>
                          <li>You'll be prompted to set up a password on your first login</li>
                        </ol>
                      </div>
                      
                      {/* W9 Section */}
                      <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-5 mb-4">
                        <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                          📋 W9 Form Submission (Required)
                        </h3>
                        <p className="text-sm text-amber-900 mb-3">
                          Before you can receive payment, you must submit a completed W9 form.
                        </p>
                        <ol className="text-sm space-y-1 list-decimal list-inside text-amber-900">
                          <li>Log into the Employee Portal using the steps above</li>
                          <li>Look for the "W9 Form" or "Tax Documents" section</li>
                          <li>Download the W9 form template (if provided) or use a standard IRS W9</li>
                          <li>Fill out the form completely and sign it</li>
                          <li>Upload a photo or scan of your completed W9</li>
                        </ol>
                        <p className="text-sm text-amber-900 mt-3 font-medium">
                          ⚠️ Important: Your W9 must be submitted before your first payday.
                        </p>
                      </div>
                      
                      {/* Checklist */}
                      <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-5 mb-4">
                        <h3 className="font-bold text-green-800 mb-2">✅ Quick Checklist</h3>
                        <ul className="text-sm space-y-1 list-disc list-inside text-green-800">
                          <li>Set up your employee portal login</li>
                          <li>Submit your W9 form</li>
                          <li>Review your hourly rate in the portal</li>
                          <li>Clock in for your first shift!</li>
                        </ul>
                      </div>
                      
                      <p className="text-gray-700 mb-2">
                        If you have any questions or need help accessing the portal, please reply to this email or contact your manager.
                      </p>
                      
                      <p className="text-gray-700 font-medium">
                        Welcome aboard! 🎉
                      </p>
                      
                      <p className="text-gray-500 text-sm mt-4">
                        — The Thrifty Curator Team
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="border-t p-4 bg-gray-50 flex justify-end">
                  <Button
                    onClick={() => setShowWelcomeEmailPreview(false)}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700"
                  >
                    Got It
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Edit Employee Modal - Extracted Component */}
          <EditEmployeeModal
            isOpen={showEditEmployee}
            onClose={() => setShowEditEmployee(false)}
            employees={employees}
            editingEmployee={editingEmployee}
            setEditingEmployee={setEditingEmployee}
            editEmployeeData={editEmployeeData}
            setEditEmployeeData={setEditEmployeeData}
            isOwner={isOwner}
            payrollSettings={payrollSettings}
            formSubmissions={formSubmissions}
            getAuthHeader={getAuthHeader}
            calculateBiweeklyPeriod={calculateBiweeklyPeriod}
            fetchData={fetchData}
            handleDownloadBlankW9={handleDownloadBlankW9}
          />


          {/* Employee Details Modal */}
          {showEmployeeDetails && selectedEmployee && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
              onClick={() => setShowEmployeeDetails(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-xl my-8"
                onClick={(e) => e.stopPropagation()}
                data-testid="employee-details-modal"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      selectedEmployee.role === 'admin' ? 'bg-[#C5A065]/20' : 'bg-[#F8C8DC]/30'
                    }`}>
                      {selectedEmployee.role === 'admin' ? (
                        <Shield className="w-6 h-6 text-[#C5A065]" />
                      ) : (
                        <User className="w-6 h-6 text-[#D48C9E]" />
                      )}
                    </div>
                    <div>
                      <h2 className="font-playfair text-xl font-bold text-[#333]">{selectedEmployee.name}</h2>
                      <p className="text-sm text-[#888]">{selectedEmployee.role}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEmployeeDetails(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Employee Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-[#F9F6F7] rounded-xl">
                    <div className="flex items-center gap-2 text-[#888] mb-1">
                      <Mail className="w-4 h-4" />
                      <span className="text-xs">Email</span>
                    </div>
                    <p className="font-medium text-[#333]">{selectedEmployee.email}</p>
                  </div>
                  <div className="p-4 bg-[#F9F6F7] rounded-xl">
                    <div className="flex items-center gap-2 text-[#888] mb-1">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-xs">Hourly Rate</span>
                    </div>
                    <p className="font-medium text-[#333]">
                      {selectedEmployee.hourly_rate ? `$${selectedEmployee.hourly_rate.toFixed(2)}/hr` : 'Not set'}
                    </p>
                  </div>
                  <div className="p-4 bg-[#F9F6F7] rounded-xl">
                    <div className="flex items-center gap-2 text-[#888] mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-xs">Joined</span>
                    </div>
                    <p className="font-medium text-[#333]">{formatDateTime(selectedEmployee.created_at)}</p>
                  </div>
                  <div className="p-4 bg-[#C5A065]/10 rounded-xl">
                    <div className="flex items-center gap-2 text-[#888] mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-xs">Total Hours</span>
                    </div>
                    <p className="font-bold text-[#C5A065] text-lg">
                      {formatHoursToHMS(parseFloat(getEmployeeStats(selectedEmployee.id).totalHours))}
                    </p>
                  </div>
                </div>

                {/* Recent Shifts */}
                <div>
                  <h3 className="font-semibold text-[#333] mb-3">Recent Shifts</h3>
                  {loadingEmployeeDetails ? (
                    <p className="text-center text-[#888] py-4">Loading...</p>
                  ) : employeeShifts.length === 0 ? (
                    <p className="text-center text-[#888] py-4">No shifts recorded</p>
                  ) : (
                    <div className="overflow-x-auto max-h-60 overflow-y-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Clock In</th>
                            <th>Clock Out</th>
                            <th>Hours</th>
                            <th>Rate</th>
                            <th>Edit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeShifts.slice(0, 10).map((shift) => (
                            <tr key={shift.id}>
                              <td>{shift.clock_in ? new Date(shift.clock_in).toLocaleDateString() : 'N/A'}</td>
                              <td>{shift.clock_in ? new Date(shift.clock_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}</td>
                              <td>
                                {shift.clock_out 
                                  ? new Date(shift.clock_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                                  : <span className="text-green-500">Active</span>
                                }
                              </td>
                              <td>
                                {shift.total_hours 
                                  ? formatHoursToHMS(shift.total_hours)
                                  : '-'
                                }
                              </td>
                              <td>
                                {shift.hourly_rate 
                                  ? <span className="text-[#10B981] font-medium">${shift.hourly_rate.toFixed(2)}</span>
                                  : <span className="text-[#888] text-xs" title="Using employee's current rate">${(selectedEmployee?.hourly_rate || payrollSettings?.default_hourly_rate || 20).toFixed(2)}</span>
                                }
                              </td>
                              <td>
                                <button
                                  onClick={() => {
                                    setShowEmployeeDetails(false);
                                    handleEditEntry(shift);
                                  }}
                                  className="text-[#C5A065] hover:text-[#9A7B4F] p-1"
                                  title="Edit shift"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                {selectedEmployee.role !== 'admin' && (
                  <div className="mt-6 pt-4 border-t border-[#eee] flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowEmployeeDetails(false);
                        setEditingRateId(selectedEmployee.id);
                        setEditingRateValue(selectedEmployee.hourly_rate?.toString() || "");
                      }}
                      className="flex items-center gap-1"
                    >
                      <DollarSign className="w-3 h-3" />
                      Edit Rate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowEmployeeDetails(false);
                        setShowAddEntry(true);
                        setNewEntryData({ ...newEntryData, employee_id: selectedEmployee.id });
                      }}
                      className="flex items-center gap-1"
                    >
                      <Clock className="w-3 h-3" />
                      Add Shift
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowEmployeeDetails(false);
                        handleDeleteEmployee(selectedEmployee.id, selectedEmployee.name);
                      }}
                      className="flex items-center gap-1 text-red-500 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </Button>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}

          {/* Shift Report Modal - Extracted Component */}
          <ShiftReportModal
            isOpen={showReport}
            onClose={() => setShowReport(false)}
            getAuthHeader={getAuthHeader}
            employees={employees}
            formatDateTime={formatDateTime}
            formatDate={formatDate}
          />


          {/* ====== PAGE-BASED DASHBOARD ====== */}

          {/* Back button when not on home */}
          {activePage !== "home" && (
            <button
              onClick={() => {
                if (activePage === "operations" && operationsSubPage) {
                  setOperationsSubPage(null);
                } else {
                  setActivePage("home");
                }
              }}
              className="flex items-center gap-2 text-sm text-white/50 hover:text-white/80 transition-colors mb-4"
              data-testid="admin-back-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}

          {/* HOME PAGE */}
          {activePage === "home" && (
            <div className="space-y-6">
              {/* All Employees - always visible on home */}
              <div data-testid="employees-section">
                <AllEmployeesSection
                  employees={employees}
                  employeeClockStatuses={employeeClockStatuses}
                  payrollSettings={payrollSettings}
                  getAuthHeader={getAuthHeader}
                  formatDateTime={formatDateTime}
                  onViewEmployeePortal={handleViewEmployeePortal}
                  onRefreshEmployees={fetchData}
                  onDownloadBlankW9={handleDownloadBlankW9}
                  isOwner={isOwner}
                />
              </div>

              {/* Hours by Employee - always visible on home */}
              <div data-testid="hours-section">
                <HoursByEmployeeSection
                  timeEntries={timeEntries}
                  employees={employees}
                  formatDateTime={formatDateTime}
                  onAddEntry={() => setShowAddEntry(true)}
                  onEditEntry={handleEditEntry}
                  onDeleteEntry={handleDeleteEntry}
                  payPeriodStart={payrollSettings.pay_period_start_date}
                />
              </div>

              {/* Navigation Tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-nav-tiles">
                {[
                  { id: "team", label: "Team", desc: `${employees.length} members`, icon: Users, color: "#00D4FF" },
                  { id: "payroll", label: "Payroll", desc: "Earnings & payments", icon: DollarSign, color: "#8B5CF6" },
                  { id: "operations", label: "Operations", desc: "Pull list & sales", icon: Package, color: "#FFB800" },
                  { id: "forms", label: "Forms", desc: `${formsSummary.total_new || 0} new`, icon: FileText, color: "#FF1493" },
                  { id: "hiring", label: "Hiring", desc: "Applications", icon: ClipboardCheck, color: "#8B5CF6" },
                  { id: "messages", label: "Messages", desc: adminUnreadMessageCount > 0 ? `${adminUnreadMessageCount} unread` : "Messaging", icon: MessageSquare, color: "#10B981" },
                  { id: "training", label: "Training", desc: "Materials", icon: Video, color: "#F59E0B" },
                  { id: "ai", label: "AI Assistant", desc: "Business & listing", icon: Monitor, color: "#00D4FF" },
                  { id: "files", label: "Business Files", desc: "Documents vault", icon: FolderOpen, color: "#10B981" },
                  { id: "whatnot", label: "Whatnot", desc: "Shows & schedule", icon: Tv, color: "#FF6B35" },
                ].map(tile => (
                  <button
                    key={tile.id}
                    onClick={() => setActivePage(tile.id)}
                    className="group relative flex flex-col items-start p-4 rounded-xl border border-white/[0.06] bg-[#0f0f1a] hover:bg-white/[0.04] hover:border-white/[0.12] transition-all text-left"
                    data-testid={`nav-tile-${tile.id}`}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: `${tile.color}15` }}>
                      <tile.icon className="w-4.5 h-4.5" style={{ color: tile.color }} />
                    </div>
                    <span className="text-sm font-semibold text-white/85">{tile.label}</span>
                    <span className="text-xs text-white/35 mt-0.5">{tile.desc}</span>
                    {tile.id === "messages" && adminUnreadMessageCount > 0 && (
                      <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#10B981]" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* TEAM MANAGEMENT PAGE */}
          {activePage === "team" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-white/90">Team Management</h2>
              <div data-testid="employees-section">
                <AllEmployeesSection
                  employees={employees}
                  employeeClockStatuses={employeeClockStatuses}
                  payrollSettings={payrollSettings}
                  getAuthHeader={getAuthHeader}
                  formatDateTime={formatDateTime}
                  onViewEmployeePortal={handleViewEmployeePortal}
                  onRefreshEmployees={fetchData}
                  onDownloadBlankW9={handleDownloadBlankW9}
                  isOwner={isOwner}
                />
              </div>
              <div data-testid="hours-section">
                <HoursByEmployeeSection
                  timeEntries={timeEntries}
                  employees={employees}
                  formatDateTime={formatDateTime}
                  onAddEntry={() => setShowAddEntry(true)}
                  onEditEntry={handleEditEntry}
                  onDeleteEntry={handleDeleteEntry}
                  payPeriodStart={payrollSettings.pay_period_start_date}
                />
              </div>
              <PasswordManagementSection token={localStorage.getItem("token")} />
              <div data-testid="terminations-section">
                <EmployeeTerminationsSection
                  employees={employees}
                  getAuthHeader={getAuthHeader}
                  onEmployeeTerminated={fetchData}
                />
              </div>
            </div>
          )}

          {/* PAYROLL PAGE */}
          {activePage === "payroll" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-white/90">Payroll & Payments</h2>
              <div className="dashboard-card" data-testid="payroll-summary">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#00D4FF]/10 to-[#00A8CC]/5 rounded-lg border border-[#00D4FF]/20">
                    <Clock className="w-4 h-4 text-[#00A8CC]" />
                    <div>
                      <p className="text-lg font-bold text-[#00A8CC]" data-testid="period-payroll">
                        ${payrollSummary.current_period?.amount?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-[10px] text-[#666] leading-tight">This Period</p>
                    </div>
                  </div>
                  {payrollSummary.outstanding_amount > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#F59E0B]/10 to-[#D97706]/5 rounded-lg border border-[#F59E0B]/20">
                      <AlertCircle className="w-4 h-4 text-[#D97706]" />
                      <div>
                        <p className="text-lg font-bold text-[#D97706]" data-testid="outstanding-amount">
                          ${payrollSummary.outstanding_amount?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-[10px] text-[#666] leading-tight">Unpaid</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#10B981]/10 to-[#059669]/5 rounded-lg border border-[#10B981]/20">
                    <CheckCircle className="w-4 h-4 text-[#059669]" />
                    <div>
                      <p className="text-lg font-bold text-[#059669]" data-testid="month-total">
                        ${payrollSummary.month_total?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-[10px] text-[#666] leading-tight">Paid This Month</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#8B5CF6]/10 to-[#6D28D9]/5 rounded-lg border border-[#8B5CF6]/20">
                    <TrendingUp className="w-4 h-4 text-[#6D28D9]" />
                    <div>
                      <p className="text-lg font-bold text-[#6D28D9]" data-testid="year-total">
                        ${payrollSummary.year_total?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-[10px] text-[#666] leading-tight">Paid This Year</p>
                    </div>
                  </div>
                  <div className="ml-auto text-xs text-[#888]">
                    {payrollSummary.current_period?.start && payrollSummary.current_period?.end ? (
                      `${formatPeriodDay(payrollSummary.current_period.start)} - ${formatPeriodDay(payrollSummary.current_period.end)}`
                    ) : ''}
                  </div>
                </div>
                <PayrollHistorySection 
                  employees={employees}
                  getAuthHeader={getAuthHeader}
                  formatHoursToHMS={formatHoursToHMS}
                  roundHoursToMinute={roundHoursToMinute}
                  isCollapsible={false}
                  compactMode={false}
                />
              </div>
              <PaymentRecordsSection getAuthHeader={getAuthHeader} />
            </div>
          )}

          {/* OPERATIONS PAGE */}
          {activePage === "operations" && (
            <div className="space-y-6">
              {/* Back to tiles when in a sub-page */}
              {operationsSubPage ? (
                <>
                  {operationsSubPage === "orders" && (
                    <div className="space-y-6">
                      <h2 className="text-lg font-semibold text-white/90">Orders & Pull List</h2>
                      <CSVUpdateButton getAuthHeader={getAuthHeader} />
                      <ShippingLabelsSection getAuthHeader={getAuthHeader} />
                      <OrderAssignmentSection getAuthHeader={getAuthHeader} employees={employees} />
                      <PullListSection getAuthHeader={getAuthHeader} />
                    </div>
                  )}
                  
                  {operationsSubPage === "mileage" && (
                    <div className="space-y-6">
                      <h2 className="text-lg font-semibold text-white/90">Mileage Tracker</h2>
                      <GPSMileageTracker 
                        ref={gpsTrackerRef}
                        getAuthHeader={getAuthHeader}
                        onTripStateChange={setTripState}
                      />
                    </div>
                  )}
                  
                  {operationsSubPage === "sales" && (
                    <div className="space-y-6">
                      <h2 className="text-lg font-semibold text-white/90">Sales Data</h2>
                      <SalesDataSection getAuthHeader={getAuthHeader} />
                    </div>
                  )}
                  
                  {operationsSubPage === "taxes" && (
                    <div className="space-y-6">
                      <h2 className="text-lg font-semibold text-white/90">Taxes</h2>
                      <TaxesSection getAuthHeader={getAuthHeader} />
                      <TaxReturnsArchiveSection getAuthHeader={getAuthHeader} />
                    </div>
                  )}

                  {operationsSubPage === "widgets" && (
                    <div className="space-y-6">
                      <h2 className="text-lg font-semibold text-white/90">Home Screen Widgets</h2>
                      <WidgetScriptsSection getAuthHeader={getAuthHeader} />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h2 className="text-lg font-semibold text-white/90">Operations</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: "orders", label: "Orders & Pull List", Icon: Package, desc: "Labels, assignments, pull list" },
                      { id: "mileage", label: "Mileage", Icon: Car, desc: "GPS trips & tax deductions" },
                      { id: "sales", label: "Sales Data", Icon: BarChart3, desc: "CSV import & analytics" },
                      { id: "taxes", label: "Taxes", Icon: Receipt, desc: "Tax reports & returns" },
                      { id: "widgets", label: "Widgets", Icon: LayoutGrid, desc: "Scriptable home-screen scripts" },
                    ].map(tile => (
                      <button
                        key={tile.id}
                        onClick={() => setOperationsSubPage(tile.id)}
                        className="flex flex-col items-center gap-2 p-5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl transition-all text-center group"
                        data-testid={`ops-tile-${tile.id}`}
                      >
                        <tile.Icon className="w-6 h-6 text-emerald-400" />
                        <span className="text-sm font-semibold text-white/90 group-hover:text-white">{tile.label}</span>
                        <span className="text-xs text-white/40">{tile.desc}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* FORMS PAGE */}
          {activePage === "forms" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-white/90">Forms & Submissions</h2>
              <div data-testid="form-submissions-section">
                <FormSubmissionsSection
                  formSubmissions={formSubmissions}
                  formsSummary={formsSummary}
                  loadingForms={loadingForms}
                  fetchFormSubmissions={fetchFormSubmissions}
                  onViewSubmission={(submission) => {
                    setSelectedSubmission(submission);
                    setShowSubmissionDetails(true);
                  }}
                  onDeleteSubmission={handleDeleteSubmission}
                  onDownloadSubmission={handleDownloadSubmission}
                  formatSubmissionDate={formatSubmissionDate}
                  getStatusBadge={getStatusBadge}
                  paymentMethodChanges={paymentMethodChanges}
                  fetchPaymentMethodChanges={fetchPaymentMethodChanges}
                  itemAdditions={itemAdditions}
                  fetchItemAdditions={fetchItemAdditions}
                  getAuthHeader={getAuthHeader}
                  onViewConsignorPortal={handleViewConsignorPortal}
                />
              </div>
            </div>
          )}

          {/* HIRING PAGE */}
          {activePage === "hiring" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-white/90">Hiring</h2>
              <div data-testid="all-applications-section">
                <AllApplicationsSection getAuthHeader={getAuthHeader} refreshKey={refreshKey} />
              </div>
              <div data-testid="send-application-link-section">
                <SendApplicationLinkSection getAuthHeader={getAuthHeader} refreshKey={refreshKey} />
              </div>
              <div data-testid="pending-documents-section">
                <PendingDocumentsSection getAuthHeader={getAuthHeader} refreshKey={refreshKey} />
              </div>
              <div data-testid="applicant-tests-section">
                <ApplicantTestsSection getAuthHeader={getAuthHeader} />
              </div>
              <div data-testid="interview-scheduler-section">
                <InterviewSchedulerSection getAuthHeader={getAuthHeader} />
              </div>
            </div>
          )}

          {/* MESSAGES PAGE */}
          {activePage === "messages" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-white/90">Messages</h2>
              <div data-testid="messages-section">
                <MessagesSection />
              </div>
            </div>
          )}

          {/* TRAINING PAGE */}
          {activePage === "training" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-white/90">Training</h2>
              <TrainingSection getAuthHeader={getAuthHeader} isAdmin={true} />
            </div>
          )}

          {/* AI ASSISTANT PAGE */}
          {activePage === "ai" && (
            <AIAssistant token={localStorage.getItem("token")} isDark={isDark} fullPage={true} />
          )}

          {activePage === "files" && (
            <BusinessFilesPage getAuthHeader={getAuthHeader} />
          )}

          {/* WHATNOT SHOWS PAGE */}
          {activePage === "whatnot" && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-white/90">Whatnot Shows</h2>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="whatnot-admin-page">
                <WhatnotShowsSection token={localStorage.getItem("token")} />
              </div>
            </div>
          )}

          {/* Submission Details Modal */}
          {showSubmissionDetails && selectedSubmission && (
            <FormSubmissionModal
              submission={selectedSubmission}
              onClose={() => setShowSubmissionDetails(false)}
              onDelete={handleDeleteSubmission}
              onDownload={handleDownloadSubmission}
              onUpdateStatus={handleUpdateSubmissionStatus}
              updatingStatus={updatingStatus}
              formatSubmissionDate={formatSubmissionDate}
              refreshData={() => {
                fetchFormSubmissions();
                fetchItemAdditions();
              }}
            />
          )}

          {/* Employee Portal View Modal - Shows exact same view as employee sees */}
          <EmployeePortalViewModal
            isOpen={showEmployeePortal}
            onClose={() => setShowEmployeePortal(false)}
            employee={viewingEmployee}
            portalData={employeePortalData}
            clockStatus={employeeClockStatus}
          />

          {/* Consignor Portal View Modal */}
          <ConsignorPortalViewModal
            isOpen={showConsignorPortal}
            onClose={() => setShowConsignorPortal(false)}
            consignor={viewingConsignor}
            portalData={consignorPortalData}
            loading={loadingConsignorPortal}
          />

          {/* Employee Shifts Modal - Extracted Component */}
          <EmployeeShiftsModal
            isOpen={!!showEmployeeShiftsModal}
            employee={showEmployeeShiftsModal}
            onClose={() => setShowEmployeeShiftsModal(null)}
            shifts={employeeShifts}
            loadingShifts={loadingShifts}
            getAuthHeader={getAuthHeader}
            onShiftAdded={() => handleViewEmployeeShifts(showEmployeeShiftsModal)}
            onShiftDeleted={() => handleViewEmployeeShifts(showEmployeeShiftsModal)}
            formatDateTime={formatDateTime}
          />

          {/* Edit Time Entry Modal - Extracted Component */}
          <TimeEntryModal
            isOpen={showEditEntry && !!editingEntry}
            onClose={() => setShowEditEntry(false)}
            mode="edit"
            entry={editingEntry}
            employees={employees}
            getAuthHeader={getAuthHeader}
            onSuccess={fetchData}
          />

          {/* Add Time Entry Modal - Extracted Component */}
          <TimeEntryModal
            isOpen={showAddEntry}
            onClose={() => setShowAddEntry(false)}
            mode="add"
            entry={null}
            employees={employees}
            getAuthHeader={getAuthHeader}
            onSuccess={fetchData}
          />

          {/* Payroll Modal */}
          {showPayroll && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
              onClick={() => setShowPayroll(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-6 w-full max-w-4xl shadow-xl my-8"
                onClick={(e) => e.stopPropagation()}
                data-testid="payroll-modal"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#C5A065]/20 rounded-full flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-[#C5A065]" />
                    </div>
                    <div>
                      <h2 className="font-playfair text-xl font-bold text-[#333]">Payroll</h2>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowPayroll(false)}
                      className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>

                {/* Period Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="form-group">
                    <Label className="form-label">Period Type</Label>
                    <Select
                      value={payrollFilters.period_type}
                      onValueChange={(value) => setPayrollFilters({ ...payrollFilters, period_type: value })}
                    >
                      <SelectTrigger className="form-input" data-testid="payroll-period-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="biweekly">Biweekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="custom">Custom Range</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {payrollFilters.period_type !== "custom" ? (
                    <div className="form-group">
                      <Label className="form-label">Period</Label>
                      <Select
                        value={payrollFilters.period_index.toString()}
                        onValueChange={(value) => setPayrollFilters({ ...payrollFilters, period_index: parseInt(value) })}
                      >
                        <SelectTrigger className="form-input" data-testid="payroll-period-index">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">{getPeriodLabel(payrollFilters.period_type, 0)}</SelectItem>
                          <SelectItem value="-1">{getPeriodLabel(payrollFilters.period_type, -1)}</SelectItem>
                          <SelectItem value="-2">{getPeriodLabel(payrollFilters.period_type, -2)}</SelectItem>
                          <SelectItem value="-3">{getPeriodLabel(payrollFilters.period_type, -3)}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <div className="form-group">
                        <Label className="form-label">Start Date</Label>
                        <Input
                          type="date"
                          value={payrollFilters.custom_start}
                          onChange={(e) => setPayrollFilters({ ...payrollFilters, custom_start: e.target.value })}
                          className="form-input"
                          data-testid="payroll-custom-start"
                        />
                      </div>
                      <div className="form-group">
                        <Label className="form-label">End Date</Label>
                        <Input
                          type="date"
                          value={payrollFilters.custom_end}
                          onChange={(e) => setPayrollFilters({ ...payrollFilters, custom_end: e.target.value })}
                          className="form-input"
                          data-testid="payroll-custom-end"
                        />
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <Label className="form-label">Default Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={payrollFilters.hourly_rate}
                      onChange={(e) => setPayrollFilters({ ...payrollFilters, hourly_rate: e.target.value })}
                      className="form-input"
                      placeholder="15.00"
                      data-testid="payroll-hourly-rate"
                    />
                    <p className="text-xs text-[#888] mt-1">Used for employees without individual rates</p>
                  </div>

                  <div className="form-group">
                    <Label className="form-label">Employee (Optional)</Label>
                    <Select
                      value={payrollFilters.employee_id || "all"}
                      onValueChange={(value) => setPayrollFilters({ ...payrollFilters, employee_id: value === "all" ? "" : value })}
                    >
                      <SelectTrigger className="form-input" data-testid="payroll-employee-filter">
                        <SelectValue placeholder="All Employees" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Employees</SelectItem>
                        {employees.filter(e => e.role !== 'admin').map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  <Button
                    onClick={handleGeneratePayrollReport}
                    disabled={payrollLoading || (payrollFilters.period_type === "custom" && (!payrollFilters.custom_start || !payrollFilters.custom_end))}
                    className="btn-primary flex items-center gap-2"
                    data-testid="generate-payroll-btn"
                  >
                    <CalendarDays className="w-4 h-4" />
                    {payrollLoading ? "Generating..." : "Generate Report"}
                  </Button>
                </div>

                {/* Payroll Report Results */}
                {payrollReport && (
                  <div className="space-y-6" data-testid="payroll-report-results">
                    {/* Period Header */}
                    <div className="bg-gradient-to-r from-[#C5A065]/10 to-[#F8C8DC]/10 rounded-xl p-4">
                      <h3 className="font-semibold text-[#333]">
                        {payrollReport.period.start_formatted} - {payrollReport.period.end_formatted}
                      </h3>
                      <p className="text-sm text-[#666]">
                        Default Rate: ${payrollReport.settings.default_hourly_rate?.toFixed(2) || '15.00'}/hr
                        {payrollReport.settings.uses_individual_rates && (
                          <span className="ml-2 text-[#C5A065]">(individual rates applied)</span>
                        )}
                      </p>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-[#F9F6F7] rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-[#333]">{payrollReport.summary.total_employees}</p>
                        <p className="text-sm text-[#888]">Employees</p>
                      </div>
                      <div className="bg-[#F9F6F7] rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-[#333]">{formatHoursToHMS(payrollReport.summary.total_hours)}</p>
                        <p className="text-sm text-[#888]">Total Hours</p>
                      </div>
                      <div className="bg-[#F9F6F7] rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-[#333]">{payrollReport.summary.total_shifts}</p>
                        <p className="text-sm text-[#888]">Total Shifts</p>
                      </div>
                      <div className="bg-[#C5A065]/10 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-[#C5A065]">${payrollReport.summary.total_wages.toFixed(2)}</p>
                        <p className="text-sm text-[#888]">Total Wages</p>
                      </div>
                    </div>

                    {/* Employee Breakdown */}
                    {payrollReport.employees.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Employee</th>
                              <th>Hours</th>
                              <th>Shifts</th>
                              <th>Rate Breakdown</th>
                              <th>Gross Wages</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payrollReport.employees.map((emp) => (
                              <tr key={emp.user_id}>
                                <td>
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-[#F8C8DC]/30 rounded-full flex items-center justify-center">
                                      <User className="w-4 h-4 text-[#D48C9E]" />
                                    </div>
                                    <div>
                                      <span>{emp.name}</span>
                                      {emp.has_custom_rate && (
                                        <span className="ml-1 text-xs text-[#C5A065]">★</span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>{formatHoursToHMS(emp.total_hours)}</td>
                                <td>{emp.total_shifts}</td>
                                <td className={emp.has_custom_rate ? 'text-[#C5A065] font-medium' : ''}>
                                  {emp.rate_breakdown && emp.rate_breakdown.length > 0 ? (
                                    <div className="space-y-0.5">
                                      {emp.rate_breakdown.map((rb, i) => (
                                        <div key={i} className="text-xs">
                                          <span className="font-medium">{formatHoursToHMS(rb.hours)}</span>
                                          <span className="text-[#888]"> @ </span>
                                          <span className="text-[#C5A065]">${rb.rate.toFixed(2)}</span>
                                          <span className="text-[#888]"> = ${rb.subtotal.toFixed(2)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span>${emp.hourly_rate.toFixed(2)}</span>
                                  )}
                                </td>
                                <td className="font-semibold text-[#C5A065]">${emp.gross_wages.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center text-[#888] py-8">No data found for this period</p>
                    )}
                  </div>
                )}
                
                {/* Close Button */}
                <div className="flex justify-end mt-6 pt-4 border-t border-[#eee]">
                  <Button
                    variant="outline"
                    onClick={() => setShowPayroll(false)}
                  >
                    Close
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Payroll Settings Modal */}
          {showPayrollSettings && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
              onClick={() => setShowPayrollSettings(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="payroll-settings-modal"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-playfair text-xl font-bold text-[#333]">Payroll Settings</h2>
                  <button
                    onClick={() => setShowPayrollSettings(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                <form onSubmit={handleSavePayrollSettings}>
                  <div className="form-group">
                    <Label className="form-label">Pay Period Start Date</Label>
                    <Input
                      type="date"
                      value={payrollSettings.pay_period_start_date}
                      onChange={(e) => setPayrollSettings({ ...payrollSettings, pay_period_start_date: e.target.value })}
                      required
                      className="form-input"
                      data-testid="payroll-settings-start-date"
                    />
                    <p className="text-xs text-[#888] mt-1">
                      This date is used to calculate biweekly pay periods. Choose the first day of any pay period.
                    </p>
                  </div>

                  <div className="form-group">
                    <Label className="form-label">Default Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={payrollSettings.default_hourly_rate}
                      onChange={(e) => setPayrollSettings({ ...payrollSettings, default_hourly_rate: parseFloat(e.target.value) || 0 })}
                      required
                      className="form-input"
                      data-testid="payroll-settings-hourly-rate"
                    />
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowPayrollSettings(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={savingSettings}
                      className="btn-primary flex-1"
                      data-testid="save-payroll-settings-btn"
                    >
                      {savingSettings ? "Saving..." : "Save Settings"}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}

          {/* Email Settings Modal */}
          {showEmailSettings && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
              onClick={() => setShowEmailSettings(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="email-settings-modal"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-playfair text-xl font-bold text-[#333]">Email Notifications</h2>
                  <button
                    onClick={() => setShowEmailSettings(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Status Badge */}
                <div className={`p-4 rounded-xl mb-6 ${emailStatus.enabled ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${emailStatus.enabled ? 'bg-green-500' : 'bg-amber-500'}`}></div>
                    <div>
                      <p className={`font-semibold ${emailStatus.enabled ? 'text-green-700' : 'text-amber-700'}`}>
                        {emailStatus.enabled ? 'Email Sending Active' : 'Running in Test Mode (MOCKED)'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{emailStatus.message}</p>
                    </div>
                  </div>
                </div>

                {/* Test Email Section */}
                <div className="space-y-4">
                  <div>
                    <Label className="form-label">Send Test Email</Label>
                    <p className="text-xs text-[#888] mb-2">
                      {emailStatus.enabled 
                        ? "Enter an email address to receive a test notification." 
                        : "In test mode, emails are logged to the server console instead of being sent."}
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={testEmailAddress}
                        onChange={(e) => setTestEmailAddress(e.target.value)}
                        className="form-input flex-1"
                        data-testid="test-email-input"
                      />
                      <Button
                        onClick={handleSendTestEmail}
                        disabled={sendingTestEmail || !testEmailAddress}
                        className="btn-primary"
                        data-testid="send-test-email-btn"
                      >
                        {sendingTestEmail ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                        <span className="ml-2">{sendingTestEmail ? "Sending..." : "Send Test"}</span>
                      </Button>
                    </div>
                  </div>

                  {/* Setup Instructions */}
                  {!emailStatus.enabled && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                      <h3 className="font-semibold text-[#333] mb-2 flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Setup Instructions
                      </h3>
                      <p className="text-sm text-[#666] mb-3">
                        To enable real email sending, add a Resend API key:
                      </p>
                      <ol className="text-sm text-[#666] space-y-2 list-decimal list-inside">
                        <li>Go to <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:underline">resend.com</a> and sign up</li>
                        <li>Navigate to <strong>API Keys</strong> in the dashboard</li>
                        <li>Click <strong>Create API Key</strong></li>
                        <li>Copy the key (starts with <code className="bg-gray-200 px-1 rounded">re_</code>)</li>
                        <li>Add to your server environment variables</li>
                      </ol>
                      <div className="mt-3 p-2 bg-gray-100 rounded font-mono text-xs">
                        RESEND_API_KEY=re_your_key_here
                      </div>
                    </div>
                  )}

                  {/* Email Types Info */}
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                    <h3 className="font-semibold text-blue-800 mb-2">Automated Notifications</h3>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• New consignment agreement confirmations</li>
                      <li>• Item addition confirmations</li>
                      <li>• Account information update confirmations</li>
                      <li>• Approval/rejection notifications from admin</li>
                    </ul>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowEmailSettings(false)}
                  >
                    Close
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* W-9 Viewer Modal - Extracted Component */}
          <W9ViewerModal
            isOpen={showW9ViewerModal}
            onClose={closeW9Viewer}
            viewingW9={viewingW9}
            employeeW9List={employeeW9List}
            selectedW9Index={selectedW9Index}
            onSelectW9={handleSelectW9}
            loading={loadingW9Viewer}
            fromPortal={w9ViewerFromPortal}
            onApproveDoc={handleApproveW9Doc}
            onDeleteDoc={handleDeleteW9Doc}
            onUploadW9={async (employeeId, file) => {
              await handleW9Upload(employeeId, file);
              handleViewW9(employeeId, viewingW9?.employeeName);
            }}
            getAuthHeader={getAuthHeader}
          />
        </div>
      </main>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="hidden md:flex fixed bottom-16 left-1/2 transform -translate-x-[75%] ml-[-24px] z-50 w-12 h-12 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white rounded-full shadow-lg hover:shadow-xl hover:shadow-[#00D4FF]/30 transition-all items-center justify-center"
            data-testid="back-to-top-btn"
            aria-label="Back to top"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Portal W-9 Modal - Dark Theme (for Employee Portal view) - Extracted Component */}
      <PortalW9Modal
        isOpen={showPortalW9Modal}
        onClose={() => setShowPortalW9Modal(false)}
        employee={viewingEmployee}
        documents={portalW9Docs}
        loading={loadingPortalW9}
        onPreview={handlePreviewPortalW9}
        onDownload={handleDownloadPortalW9}
        onDownloadBlankW9={handleDownloadBlankW9}
        previewingDoc={previewingPortalW9}
        onClosePreview={() => setPreviewingPortalW9(null)}
      />
      
      {/* Full-Screen Messages Modal */}
      <AnimatePresence>
        {showFullScreenMessages && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-[9999] flex flex-col overflow-hidden`}
            style={{ 
              paddingTop: 'env(safe-area-inset-top, 0px)',
              backgroundColor: messagesTheme === 'dark' ? '#1A1A2E' : '#ffffff'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#1A1A2E] to-[#2D2D44]">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-[#00D4FF]" />
                <h2 className="text-xl font-bold text-white">Messages</h2>
              </div>
              <div className="flex items-center gap-2">
                {/* Theme toggle */}
                <button
                  onClick={() => {
                    const newTheme = messagesTheme === 'dark' ? 'light' : 'dark';
                    setMessagesTheme(newTheme);
                    localStorage.setItem('thrifty_curator_messages_theme', newTheme);
                  }}
                  className={`p-2 rounded-lg ${messagesTheme === 'dark' ? 'bg-white/10 text-yellow-400' : 'bg-white/20 text-white'} hover:bg-white/30`}
                  title={messagesTheme === 'dark' ? "Switch to light mode" : "Switch to dark mode"}
                  data-testid="admin-theme-toggle-btn"
                >
                  {messagesTheme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                {/* Mute toggle */}
                <button
                  onClick={() => {
                    const newMuted = !messagesMuted;
                    setMessagesMuted(newMuted);
                    localStorage.setItem('thrifty_curator_admin_messages_muted', newMuted.toString());
                    toast.success(newMuted ? "Message notifications muted" : "Message notifications unmuted");
                  }}
                  className={`p-2 rounded-lg ${messagesMuted ? 'bg-red-500/20 text-red-400' : 'bg-white/20 text-white'} hover:bg-white/30`}
                  title={messagesMuted ? "Unmute notifications" : "Mute notifications"}
                >
                  {messagesMuted ? <BellOff className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
                </button>
                {/* Close button */}
                <button
                  onClick={() => setShowFullScreenMessages(false)}
                  className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Full-screen messaging content */}
            <div className="flex-1 overflow-hidden">
              <AdminFullScreenMessaging
                muted={messagesMuted}
                onUnreadChange={setAdminUnreadMessageCount}
                currentAdminName={user?.name || "Admin"}
                theme={messagesTheme}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
